﻿/**
 * 最陽春的 shell。
 *
 * 它只做四件事：桌布、視窗框、iframe 綁定、session 存取。
 * 沒有工具列、沒有時間軸面板、沒有任何業務邏輯——**那些都是 app**。
 *
 * 這一層是刻意可拋棄的：桌面版、平板監視面板、嵌入版可以各有一個 shell，
 * 共用同一個 kernel。它越小，換掉越容易。
 */

import type {
  Bus,
  BusError,
  CatalogEntry,
  GesturePhase,
  GesturePoint,
  InitialWindow,
  InstanceSummary,
  LayoutTemplate,
  NodeDef,
  Registration,
  SessionSnapshot,
  WindowRect,
  WindowState,
} from '@qdesktop/kernel'
import { nearestCellIndex } from '@qdesktop/kernel'
import { IframeBinding } from './iframe-binding.ts'
import type { SessionStore } from './session.ts'
import { isLocale, translator, type Locale, type ShellKey } from './i18n.ts'
import { helpContent, type HelpBlock, type HelpSection } from './help.ts'

export interface ShellOptions {
  bus: Bus
  root: HTMLElement
  /** 有什麼可以開。會發布到 $sys/registry/apps 供 launcher app 讀取。 */
  catalog?: readonly CatalogEntry[]
  /** 可選的版面板型。省略時使用內建的五種。 */
  layouts?: readonly LayoutTemplate[]
  /** 提供了就會自動存檔與還原 */
  session?: SessionStore
  /** 登入閘問出來的身分。沒有登入閘就省略。 */
  account?: Account
  /**
   * 換一個桌面。給了才會出現那顆按鈕。
   *
   * shell 不知道桌面是從哪裡挑的——那是宿主的事（在這個部署裡是 QSyn 的桌面清
   * 單，別的部署裡可能根本沒有這回事）。它只知道「有沒有一條路可以換」，而那
   * 個答案只有宿主給得起。
   *
   * 網頁上不需要：那裡的網址列本來就是這條路。App 裡沒有網址列，於是一台裝置
   * 一旦連上某個桌面，就再也回不去挑選畫面。
   */
  onSwitchDesktop?: () => void
  /**
   * dock 上要不要放「?」說明按鈕。預設 true——網頁版讀者除了 shell 之外沒有
   * 別的入口。**原生 app 把它關掉**：Flutter 那邊「帳號 → 說明文件」就是這個
   * 東西的另一種呈現，dock 上再放一顆會顯得功能重複。
   */
  showHelp?: boolean
  /**
   * 每次啟動都要補齊的 app。已經在存檔裡的會跳過，所以 uid 必須固定。
   *
   * **只放關不掉的東西**——沒有畫面、使用者碰不到的那種機械。有畫面的放進來會
   * 讓「關閉」變成一句謊話：關掉它、存檔確實把它拿掉了、畫面上也說存好了，下次
   * 開機它又回來。使用者看到的是「存不起來」，而存檔其實一直是對的。
   *
   * 這裡以前放著設定面板、通知、小幫手，理由是「否則關掉 launcher 就再也開不了
   * 東西」。那句話當時成立，後來不成立了：launcher 搬進 shell 變成右鍵選單之後，
   * 任何 app 都隨時開得起來，沒有什麼會因為關掉一個視窗就永遠拿不回來。規則留著
   * 而理由消失了——那正是這種 bug 的長相。
   */
  systemApps?: readonly LaunchSpec[]
  /** 沒有存檔時才會開的東西 */
  defaults?: readonly LaunchSpec[]
  /**
   * 左上角那顆連線指示燈要看哪一條 topic。
   *
   * **shell 不知道那是什麼。** 它只訂閱一條路徑,把值畫成一個點:認得
   * connected / connecting / offline / unpaired 這幾個字,其餘照原樣顯示。哪一
   * 條路徑代表「連線」是宿主的政策——就跟目錄、系統 app 一樣。
   *
   * 這一層對 QSyn 一無所知(concept.md §9),而那正是為什麼這裡是一個路徑而不是
   * 一個 `qsynStatus` 布林。
   */
  statusTopic?: string
  /**
   * 這個桌面最多能開幾個元件視窗。負數或省略＝不限。
   *
   * 只算元件,不算 `systemApps`——那些是每個桌面都有的機械,不是使用者擺上去的
   * 東西。把它們算進去,會讓一個標示「20 個元件」的方案實際上只能放 16 個。
   *
   * 還原也受限:超過的不開。上限如果只擋新開的,一個從高方案降下來的桌面會永遠
   * 停在超額狀態,而那個上限對它等於不存在。沒開的會寫進主控台,不是無聲消失。
   *
   * 可以給函式,因為這個數字是跟著存檔一起從 QSyn 回來的——而那次請求就發生在
   * `Shell.start` 內部,比還原早一步、比建構晚一步。
   */
  windowLimit?: number | (() => number)
}

/**
 * 誰把這台桌面解鎖的。
 *
 * 桌面本身沒有帳號——這是**宿主的登入閘**問出來的答案，由宿主交進來。核心對
 * 「使用者」一無所知，也不該知道（原則三）。
 *
 * 帶著工作空間，是因為配對綁的是配對當下作用中的那一個。之後在 QSyn 切到別的
 * 工作空間，那邊的代理人就查不到這台桌面，而症狀是「桌面明明連著，代理人卻說
 * 碰不到」——桌面上寫著它屬於誰、配在哪，那個問題就不必查。
 */
export interface Account {
  /**
   * 這是哪一個桌面。空字串表示不是從 QSyn 的桌面清單開起來的——那樣還是能用，
   * 只是沒有地方可以同步，變更只留在這台機器上。
   */
  desktop_id: string
  /**
   * 這張桌面的名字。可能是空的——舊的宿主不會給，而少一個標籤不該讓桌面開不起來。
   *
   * 沒有它的時候，膠囊上寫的是「誰、在哪個工作空間」，兩張長得一樣的桌面因此
   * 分不出來——而一個人的三張桌面全叫「新桌面」時，那正是他要問的那一件事。
   */
  desktop?: string
  /** QSyn 的使用者 id。宿主拿它分存檔——一台機器換一個人登入，桌面就該換一個。 */
  user_id: string
  user: string
  email: string
  workspace: string
  workspace_id: string
  /** 已登入但沒配到裝置（例如權限只有 viewer）。桌面照開，代理人接不上。 */
  paired: boolean
}

export interface LaunchSpec {
  url: string
  title: string
  summary?: string
  /** 產生 uid 時的前綴 */
  slug?: string
  /** 指定 uid。還原 session 與系統 app 用，讓參照保持穩定。 */
  uid?: string
  rect?: Partial<WindowRect>
  state?: WindowState
  z?: number
  transparent?: boolean
  /** 存檔裡調整過的 rw 節點，等 app 註冊時由核心灌回去 */
  settings?: Record<string, unknown>
  /**
   * rect 是建議（預設）還是已經決定過的位置。還原時給 false——見
   * `InitialWindow.fit`。
   */
  fit?: boolean
}

interface WindowView {
  uid: string
  url: string
  frame: HTMLDivElement
  titleText: HTMLElement
  statusText: HTMLSpanElement
  iframe: HTMLIFrameElement
  tombstone: HTMLDivElement
  binding: IframeBinding
  maximise: HTMLButtonElement
  actor: HTMLSpanElement
  glowTimer: ReturnType<typeof setTimeout> | null
}

const SAVE_DEBOUNCE_MS = 500
/** 再怎麼忙也要存一次。見 scheduleSave。 */
const MAX_SAVE_WAIT_MS = 5_000
const SESSION_APP_UID = 'session'

const STYLE = `
/* 捲軸。細、半透明、沒有箭頭——它是需要的時候才該被看見的東西。
   scrollbar-width / scrollbar-color 是標準寫法（會繼承），::-webkit-scrollbar
   是給還沒跟上的 WebKit；兩份都寫，各家才會長得一樣。 */
html{scrollbar-width:thin;scrollbar-color:#ffffff33 transparent}
::-webkit-scrollbar{width:9px;height:9px}
::-webkit-scrollbar-track{background:transparent}
::-webkit-scrollbar-thumb{background:#ffffff33;border-radius:99px;
  border:2px solid transparent;background-clip:content-box}
::-webkit-scrollbar-thumb:hover{background:#ffffff5c;background-clip:content-box}
::-webkit-scrollbar-corner{background:transparent}
/* 掛載點。它只做一件事：把螢幕擺在中間。螢幕比它大的時候可以捲過去——
   在一台筆電上模擬 27 吋，總得看得到另外那一半。 */
/* user-select / tap-highlight / overscroll：三件讓一個網頁看起來像網頁的事。
   長按跳出「複製」、點一下閃一塊灰、拉到底彈回來——原生 app 都不做這些，而
   它們正是「這是一個瀏覽器」最明顯的三個破綻。
   iframe 是另一份文件，所以元件裡的文字仍然選得起來，那是對的。 */
.qd-root{position:relative;width:100%;height:100%;overflow:auto;display:flex;background:#0b0b0f;
  font:13px/1.5 system-ui,sans-serif;color:#e8e8ef;
  user-select:none;-webkit-user-select:none;-webkit-tap-highlight-color:transparent;
  overscroll-behavior:none}
/* 桌面的層次。數字小是因為它們只跟彼此比——視窗的 z 關在 .qd-layer 裡面，
   不管寫多大都爬不出來，所以這裡不需要跟它競價。
     0 視窗　1 因果連線　2 版面輔助線　3 底部膠囊　4 釘住清單　5 右鍵選單 */
/* 螢幕：這個桌面被裝在多大的一塊玻璃上。預設就是瀏覽器視窗。
   桌布畫在這裡，dock、選單、面板也都掛在這裡——**整個桌面住在螢幕裡面**，
   所以模擬一支手機的時候，看到的是手機上真正會看到的樣子，而不是一支手機
   配一條橫跨整個螢幕的 dock。 */
/* contain:paint 不是為了效能，是為了定位：它讓這個元素成為 position:fixed 子孫的
   containing block。少了它，最大化的視窗會釘在**瀏覽器視窗**上而不是這塊螢幕上，
   於是模擬一支手機的時候，最大化會蓋掉整個桌面——連螢幕外的留白一起。
   畫面上什麼都不會變：overflow:hidden 本來就在裁同一塊。 */
.qd-screen{position:relative;width:100%;height:100%;margin:auto;flex:none;overflow:hidden;
  contain:paint}
.qd-screen[data-sized]{border-radius:10px;box-shadow:0 0 0 1px #ffffff1f,0 10px 50px #000a}
/* 捲動的那一層。桌面比螢幕大的時候就有捲軸——**排到畫面外的視窗因此找得回來**,
   在這之前那是一去不回的。 */
/*
  原生捲軸藏起來，捲動照常。

  桌面上的捲軸是實心的、會佔掉版面的寬度；手機上那條是浮在內容上、停手就消失的。
  這塊螢幕多半是一支手機，一條永遠站在右邊的灰槽不屬於它。

  這也順便拿掉了 scrollbar-gutter:stable。那條槽當初是為了擋一種抖動——出現捲軸
  →畫布變窄→不需要捲軸→又變寬，而畫布寬度每變一次就回報一次 viewport，那一路
  下去是一連串寫入。沒有捲軸就沒有那條槽，也就沒有那個迴圈。

  代價是**滑鼠拖不動捲軸**了。滾輪、觸控板、觸控與鍵盤都不受影響，而在一塊手機
  大小的螢幕上，前者本來就不是主要的捲動方式。
*/
.qd-scroll{position:absolute;inset:0;overflow:auto;z-index:0;scrollbar-width:none}
.qd-scroll::-webkit-scrollbar{width:0;height:0}
/* 自己畫的那一條。住在**螢幕**上而不是捲動層裡，所以它不佔版面、也不會跟著捲；
   pointer-events:none 是因為它是一個指示器，不是控制項——會動但按不動的東西比
   沒有更糟。 */
.qd-scrollhint{position:absolute;z-index:2;pointer-events:none;top:0;right:3px;width:4px;
  border-radius:99px;background:#ffffff5c;opacity:0;transition:opacity .35s ease}
.qd-scrollhint-x{top:auto;right:auto;bottom:3px;left:0;width:auto;height:4px}
/* 桌面：視窗擺得下的那塊地，一個螢幕大。它沒有自己的尺寸設定——把視窗拖出邊界
   時 overflow 會把捲動範圍撐開，桌面就跟著長。所以「排到畫面外的視窗找得回來」
   是免費的，不是又一個要設定的數字。 */
.qd-canvas{position:relative;width:100%;height:100%}
/* 捲到最底的時候多留 10px 呼吸空間——元件被拖到邊緣時，最後一顆之下有一格
   透明的緩衝就摸得到 dock 也不會被截。做法是掛一個 ::after 貼在 canvas 下
   緣的外側 10px，pointer-events:none 讓它不擋任何互動。它沒有元件擋在下面
   時就是「什麼都看不見的 10px」；真的有元件超出時，就變成該元件底部與捲軸
   底之間的那一小段空白。 */
.qd-canvas::after{content:'';position:absolute;left:0;right:0;top:100%;height:10px;
  pointer-events:none}
.qd-layer{position:absolute;inset:0;z-index:0}
.qd-win{container-type:inline-size;position:absolute;display:flex;flex-direction:column;background:transparent;border:1px solid #3a3a4a;
  border-radius:8px;box-shadow:0 8px 32px rgba(0,0,0,.45);overflow:hidden;min-width:180px;min-height:80px;
  transition:left .18s cubic-bezier(.2,.8,.3,1),top .18s cubic-bezier(.2,.8,.3,1),
    width .18s cubic-bezier(.2,.8,.3,1),height .18s cubic-bezier(.2,.8,.3,1),
    box-shadow .25s ease-out,border-color .15s}
/* 人在拖的時候不能補間，否則會黏手 */
.qd-win.qd-dragging{transition:box-shadow .25s ease-out,border-color .15s}
/* 手勢期間同樣不能補間，理由跟拖曳一樣（D25）——每一格都往目標 ease 180ms，
   手指已經到了視窗還在路上，那就是「lag」。
   跟 qd-dragging 分開是因為那條還會把 iframe 的 pointer-events 關掉，而手勢的
   觸控序列正是從 iframe 裡發出來的。 */
.qd-win.qd-gesturing{transition:box-shadow .25s ease-out,border-color .15s}

/* 藍框＝人的注意力在這；光暈＝agent 剛動過這裡。兩種意義，兩種樣子 */
.qd-win.qd-focus{border-color:#4a6ee0}
.qd-win.qd-agent{box-shadow:0 8px 32px rgba(0,0,0,.45),0 0 0 2px rgba(122,162,255,.9),
  0 0 26px 5px rgba(74,110,224,.5)}
.qd-win.qd-agent-denied{box-shadow:0 8px 32px rgba(0,0,0,.45),0 0 0 2px rgba(255,120,110,.95),
  0 0 26px 5px rgba(192,57,43,.5)}
/* 人透過某個 app 的介面做的：只是提示因果，不該跟自主行為一樣搶眼 */
.qd-win.qd-relay{box-shadow:0 8px 32px rgba(0,0,0,.45),0 0 0 1px rgba(140,170,230,.55)}
.qd-win.qd-relay .qd-actor{opacity:.85;background:rgba(90,110,150,.85)}
.qd-actor{position:absolute;right:4px;bottom:4px;z-index:4;pointer-events:none;
  padding:1px 7px;border-radius:4px;background:rgba(74,110,224,.92);color:#fff;
  font:10px/1.6 ui-monospace,monospace;opacity:0;transition:opacity .15s}
.qd-win.qd-agent .qd-actor,.qd-win.qd-agent-denied .qd-actor{opacity:1}
.qd-win.qd-agent-denied .qd-actor{background:rgba(192,57,43,.92)}
.qd-title{display:flex;align-items:center;gap:6px;padding:6px 8px;background:#2a2a38;cursor:grab;
  user-select:none;flex:0 0 auto;touch-action:none}
.qd-title:active{cursor:grabbing}
/* flex:1 讓標題吃掉多餘的空間，按鈕才會靠右。
   以前靠的是那個生命週期標籤的 margin-left:auto——但窄視窗的容器查詢會把它藏起來，
   於是那排按鈕整個滑到左邊。把對齊交給一個會消失的元素，是這種「只在某個寬度
   才壞」的錯的典型來源。min-width:0 是省略號在 flex 子項裡生效的前提。 */
.qd-title b{flex:1;min-width:0;font-weight:600;font-size:12px;overflow:hidden;
  text-overflow:ellipsis;white-space:nowrap}
/* 標題列上的生命週期。**只在不是 ready 的時候才出現**——一個在每扇窗上都寫著
   「正常」的標籤，說的是所有視窗的共同點，而共同點沒有資訊。loading 值得說，
   crashed / closed 有墓碑蓋著，ready 只是雜訊。 */
.qd-life{margin-left:auto;font-size:11px;opacity:.6}
.qd-life[hidden]{display:none}
/* 自己置中，不要靠 UA 的按鈕預設值：那份預設有 padding，而且對彩色 emoji
   （🔒）與一般文字字符（⤒ ▢ ×）給的字身寬度不一樣，於是同一排按鈕裡只有
   emoji 那顆看起來偏掉。flex 置中量的是格子，跟字形無關。 */
.qd-btn{display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;padding:0;
  border:0;background:#3a3a4a;color:#e8e8ef;width:20px;height:20px;border-radius:4px;
  cursor:pointer;line-height:1;font:12px/1 system-ui;flex:0 0 auto}
.qd-btn:hover{background:#4a4a5e}
/* 窄視窗擠不下九顆按鈕：200px 寬的時候光是按鈕本體就超過整條標題列，多的會被
   推到右邊看不見。收緊而不是藏起來——藏起來的控制項在觸控裝置上就等於沒有。 */
@container (max-width: 320px){
  .qd-title{gap:3px;padding:5px 5px}
  .qd-btn{width:17px;height:17px;font-size:10px}
  .qd-title b{font-size:11px}
  .qd-life{display:none}
}
@container (max-width: 232px){
  .qd-title b{display:none}
}
.qd-btn.qd-close:hover{background:#c0392b}
/* 內容區**不畫底色**。畫了就等於替 app 決定它長什麼樣，而想透明的 app 再也
   透不出去。每個 app 自己的頁面已經在畫自己的背景了——這裡讓開就好。 */
/* min-height:0 —— 少了它，**比 150px 矮的視窗會把自己的內容切掉**。
   flex item 的 min-height 預設是 auto（＝不得小於內容的最小高度），而 iframe 的
   預設尺寸是 HTML 規定的 300×150。所以一個 92px 高的視窗裡，這一塊縮不到 92，
   卡在 150：iframe 的 height:100% 因此也算成 150，超出的部分被 .qd-win 的
   overflow:hidden 切掉。
   症狀特別難認——app 裡的一切都是照 150px 排的，所以它「置中」的位置在框外面，
   看起來像那支元件自己沒對齊，而不是像被裁掉。桌面上的視窗多半比 150 高，所以
   這件事只在手機那種矮卡片上現形。 */
.qd-body{position:relative;flex:1 1 auto;min-height:0;background:transparent}
.qd-body iframe{display:block;width:100%;height:100%;border:0;background:transparent}
.qd-tomb{position:absolute;inset:0;display:none;flex-direction:column;align-items:center;justify-content:center;
  gap:10px;background:#1e1e28;color:#e8e8ef;font-size:13px}
.qd-tomb button{padding:6px 14px;border:0;border-radius:5px;background:#4a6ee0;color:#fff;cursor:pointer}
.qd-dragging iframe{pointer-events:none}

/* 最大化蓋住的是**螢幕**，不是桌面。
   以前是 left/top:0 加 100%，但那個 0 是**畫布**的 0——而畫布可以比螢幕高很多
   （視窗排到螢幕外時它會自己長）。所以捲一下，最大化的視窗就跟著捲出去了：它
   從來沒有蓋住螢幕，只是在還沒捲動的時候看起來像。
   position:fixed 把它釘在 .qd-screen 上（那裡的 contain:paint 就是為此）。它仍然
   住在 .qd-layer 裡，所以 dock 與選單照樣疊在它上面——一個蓋住整片螢幕的視窗
   之後，dock 是唯一還碰得到的表面（D20）。 */
.qd-win.qd-max{position:fixed;left:0!important;top:0!important;right:0!important;bottom:0!important;
  width:auto!important;height:auto!important;border-radius:0}
/* 全螢幕時，視窗**本身**還是頂到 0——這樣桌布上不會露一條黑；但把視窗列
   本身往下推 safe-area 的高度，才不會被 status bar / 瀏海蓋掉不能按。作法
   是加 padding-top：內部的按鈕（最大化、關閉、⚙…）被推下來、標題列自己
   的背景**照樣**延伸到頂邊，看起來還是整片滿版。畫布上的 iframe 因此往下
   讓一點高度給它，那正是「還在全螢幕，只是視窗列避開了 status bar」的意思。
   --qd-inset-top 由原生殼在 CSSOM 上設定（把 SafeArea inset 直接傳過來）；
   瀏覽器版走 env(safe-area-inset-top)。max 兩者取一。 */
.qd-win.qd-max > .qd-title{
  padding-top:calc(6px + max(env(safe-area-inset-top), var(--qd-inset-top, 0px)))}
/* 內容區本來就不畫底色了，所以這裡剩下的意思是「連框帶影一起拿掉」——
   給那種要浮在桌布上、自己畫外框的 app。
   標題列的底色**不跟著拿掉**：它是抓手，拖曳、最小化、關閉都在上面，
   底下又是會動的桌布，看不清楚就按不準。 */
.qd-win.qd-transparent{border-color:transparent;box-shadow:none}
/* 滑過的時候把邊框還回來。八個縮放把手在那裡，但透明視窗沒有可見的邊——
   看不見的把手等於沒有把手，症狀是「這個視窗不能拉大拉小」。 */
.qd-win.qd-transparent:hover{border-color:rgba(255,255,255,.24)}

/* 「這個工具怎麼用」——內容來自 $meta，也就是 agent 讀的同一份 */
.qd-info{display:none;position:absolute;left:0;right:0;top:33px;bottom:0;z-index:6;overflow-y:auto;
  padding:10px 12px;background:#161620f5;backdrop-filter:blur(4px);color:#dcdce8;font-size:12px}
/* max 模式的 title bar 因為 padding-top 加了 safe-area inset 變高，info /
   settings 面板的 top 也要跟著往下、不然會蓋住 title bar 的按鈕。33px 是
   一般 title bar 的內容高度（padding 6+6 + 內容 ~21），max 時再加 safe-area。 */
.qd-win.qd-max > .qd-info{
  top:calc(33px + max(env(safe-area-inset-top), var(--qd-inset-top, 0px)))}
.qd-info.qd-open{display:block}
.qd-info-lede{margin:0 0 10px;opacity:.8;line-height:1.6}
.qd-info-note{margin:0;opacity:.5}
.qd-info-row{padding:7px 0;border-top:1px solid #ffffff12}
.qd-info-row:first-of-type{border-top:0}
.qd-info-head{display:flex;align-items:center;gap:6px;flex-wrap:wrap}
.qd-info-head code{font:11px ui-monospace,monospace;color:#9fd0ff}
.qd-info-tag{padding:0 5px;border-radius:3px;background:#ffffff1a;font-size:10px;opacity:.75}
.qd-info-unit{font-size:10px;opacity:.5}
.qd-info-risk{padding:0 5px;border-radius:3px;background:#e0a33a33;color:#ffd28a;font-size:10px}
.qd-info-desc{margin:3px 0 0;opacity:.62;line-height:1.55}

/* 設定：控制項的型別完全由節點的 schema 推導，所以這裡沒有任何一個 app 的名字 */
.qd-set-group{margin:10px 0 4px;font:600 10px system-ui;letter-spacing:.05em;opacity:.45}
.qd-set-group:first-child{margin-top:0}
.qd-set-row{display:grid;grid-template-columns:88px 1fr auto;gap:5px 8px;align-items:center;
  padding:5px 0;border-top:1px solid #ffffff0f}
.qd-set-row:first-of-type{border-top:0}
.qd-set-label{font-size:11px;opacity:.7;overflow-wrap:anywhere}
.qd-set-unit{margin-left:4px;font-size:9px;opacity:.5}
.qd-set-read{font:10px ui-monospace,monospace;opacity:.55;min-width:2.5em;text-align:right}
.qd-set-note{grid-column:2/-1;margin:0;font-size:10.5px;opacity:.45;line-height:1.5}
.qd-set input[type=text],.qd-set input[type=number],.qd-set select{width:100%;box-sizing:border-box;
  padding:3px 6px;border:1px solid #3a3a4a;border-radius:4px;background:#1e1e2a;color:#dcdce8;font:11px system-ui}
.qd-set input[type=range]{width:100%}
.qd-set textarea{width:100%;box-sizing:border-box;max-height:38vh;resize:vertical;
  padding:3px 6px;border:1px solid #3a3a4a;border-radius:4px;background:#1e1e2a;color:#dcdce8;
  font:11px/1.5 ui-monospace,monospace}
.qd-set input[type=checkbox]{justify-self:start}
.qd-set .qd-bad{border-color:#d9534f}
.qd-set-err{margin-top:6px;color:#ff9b97;font-size:11px}

/* 選單/鎖定列表打開時的隱形遮罩層。iframe 的點擊不會冒泡出來,單靠 pointerdown
   或 focusin 抓不到——focusin 在某些 WKWebView 建構版本上根本不 fire。所以直接
   鋪一層透明的東西蓋在 iframe 上、吃掉那一次點擊,把彈出關掉。人打開彈出的意思
   就是要選裡面的東西,第一次點外面本來就是「先取消」,把它落到視窗上反而奇怪。 */
.qd-scrim{position:absolute;inset:0;z-index:4;background:transparent}

/* 右鍵選單。z 要在膠囊之上——它是當下最該被看到的東西 */
.qd-menu{position:absolute;z-index:5;width:300px;max-height:340px;display:flex;flex-direction:column;
  border-radius:10px;background:#1b1b26;box-shadow:0 12px 40px #000a,0 0 0 1px #ffffff1f;overflow:hidden}
.qd-menu input{flex:none;margin:0;padding:9px 11px;border:0;border-bottom:1px solid #ffffff14;
  background:transparent;color:#e8e8ef;font:13px system-ui;outline:none}
.qd-menu ul{margin:0;padding:4px;list-style:none;overflow-y:auto;flex:1}
/* touch-action: manipulation 拿掉 iOS Safari 對非按鈕元素的 300ms 點擊延遲，
   也順便告訴瀏覽器「這裡不做雙擊縮放」。沒設的話 li 在 WebView 裡有些狀況下
   click 不會 fire——桌面瀏覽器沒差，行動裝置差很大。 */
.qd-menu li{padding:6px 8px;border-radius:6px;cursor:pointer;touch-action:manipulation}
.qd-menu li[aria-selected=true]{background:#4a5bd4}
.qd-menu li b{display:block;font-weight:600;font-size:12.5px}
.qd-menu li span{display:block;font-size:10.5px;opacity:.5;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.qd-menu li[aria-selected=true] span{opacity:.8}
.qd-menu-none{padding:10px 8px;opacity:.4;font-size:12px;cursor:default}

/*
   觸控裝置上把輸入框的字級撐到 16px。

   iOS Safari 與 WKWebView 對 font-size < 16px 的 input 在 focus 時會自動放大整個
   viewport（原意是「小字看不清楚，我幫你」）。但這個殼是視窗管理介面，不是文件——
   放大之後 pinch-zoom 又常常被鎖住，於是使用者卡在放大狀態回不去。「按了新增元
   件就再也縮不回來」的路徑就是這一條：右鍵選單的搜尋欄自動 focus，字級 13px，於
   是 iOS 幫你放大整個桌面。

   hover:none + pointer:coarse 只挑觸控裝置，桌面版視覺完全不動。
*/
@media (hover: none) and (pointer: coarse) {
  .qd-menu input,
  .qd-set input[type=text],
  .qd-set input[type=number],
  .qd-set select,
  .qd-set textarea { font-size: 16px }
}

/* 最小化後停在底部的膠囊 */
/*
   底部膠囊。往內縮開系統列與瀏海。

   兩個來源取大的那一個，因為它們量的是**不同的東西**：env(safe-area-inset-*)
   講的是螢幕的瀏海與圓角，而系統列（狀態列、手勢列）不算在裡面——沒有瀏海的機
   器上那個值就是 0，於是內容會壓在時鐘上。系統列有多高只有宿主知道，所以它用
   --qd-inset-* 說。

   （這段註解住在一個樣板字串裡，所以**不能有反引號**——它會把 CSS 提早關掉，
   而錯誤會報在幾百行之外。這個坑踩過兩次了。）

   桌布仍然滿版：**背景到邊、控制項避開**。把整片桌面往內縮會在四周留下一圈黑框，
   那正是把系統列改成透明想避免的事。
*/
.qd-dock{position:absolute;left:0;right:0;bottom:0;z-index:3;display:flex;flex-wrap:nowrap;gap:6px;
  justify-content:center;align-items:center;padding:4px 8px;pointer-events:none;
  /* 貼底更貼一點——padding-bottom 只留 2px + safe-area，把整條 dock 推到手指
     最自然的邊緣範圍。以前 8px 邊距在 home indicator 上再疊一次高度，看起來
     像另一條浮動列。 */
  padding-bottom:calc(2px + max(env(safe-area-inset-bottom), var(--qd-inset-bottom, 0px)));
  /* 閒置淡出——沒互動時退場、任何觸控就浮現。opacity 是唯一動的屬性，
     不影響 layout；qd-hidden 只在 dock 沒東西可按也不算數的時候套用。 */
  transition:opacity .35s ease}
/* 使用者拖過位置以後 dock 走 left/top（見 wireDockDrag）：把 right / bottom
   拿掉才不會被兩邊拉扯。這個變體 class 由 JS 加上，CSS 只描述形狀。 */
.qd-dock.qd-dock-floating{left:auto;right:auto;bottom:auto;padding-bottom:4px;justify-content:flex-start}
/* 直式 dock——轉向按鈕按下去以後 flex-direction 換成 column，同時強制轉為
   floating（見 applyDockLayout）：直式沒有天然的貼齊邊，只能是浮動；每顆膠囊
   撐滿容器寬度，看起來像一疊按鈕而不是塞在一欄的碎片。 */
.qd-dock.qd-dock-vertical{flex-direction:column;align-items:stretch;padding:6px 4px;padding-bottom:6px}
.qd-dock.qd-dock-vertical .qd-pill{width:100%;text-align:center}
.qd-dock.qd-hidden{opacity:0}
.qd-dock.qd-empty{display:none}
/* 拖曳把手——最左邊那顆膠囊，按住拖動整條 dock 到想放的位置。位置存
   localStorage，下次重整還在。flex:none 是因為它是圖示、縮下去也沒空間可
   縮；把可縮的餘裕留給桌面名稱那顆膠囊。 */
.qd-pill.qd-dock-drag{flex:none;cursor:grab;touch-action:none;font-size:14px;line-height:1;padding:5px 10px}
.qd-pill.qd-dock-drag:active{cursor:grabbing;background:#4a5bd4;color:#fff}
/* 轉向按鈕——把手右邊那顆，一按切換 dock 方向。字用 monospace 的箭頭，
   兩個字元寬度一樣、切換的時候按鈕不會抖。同樣 flex:none。 */
.qd-pill.qd-dock-orient{flex:none;font-size:13px;line-height:1;padding:5px 10px}

/* 左上角那一排。連線與存檔並排,因為它們回答的是同一類問題——「現在還好嗎」
   ——而那類問題正是在出事的時候問的,出事的時候通常有東西蓋住了畫面。
   存檔狀態以前只在設定面板裡看得到,而把面板關掉是很普通的事。 */
.qd-status{position:absolute;z-index:4;display:flex;align-items:center;gap:6px;
  top:calc(8px + max(env(safe-area-inset-top), var(--qd-inset-top, 0px)));
  left:calc(8px + env(safe-area-inset-left))}
.qd-link{display:flex;align-items:center;gap:6px;
  padding:4px 10px;border:1px solid #ffffff1f;border-radius:999px;background:#20202cd9;
  color:#b9b9c8;font:11px system-ui;backdrop-filter:blur(6px);pointer-events:none;
  transition:opacity .3s}
/* 連上的時候退到背景:一個永遠亮著的綠燈,看久了就等於沒有燈。 */
.qd-link[data-state=connected]{opacity:.4}
.qd-link>i{width:7px;height:7px;border-radius:50%;background:#6b6b7a;flex:none}
.qd-link[data-state=connected]>i{background:#35c46a}
.qd-link[data-state=connecting]>i{background:#e0a33a}
.qd-link[data-state=offline]>i{background:#d9534f}
/* 存檔狀態。跟連線同一種膠囊,因為它們是同一排東西;存好了就退到背景,理由也
   一樣——一個永遠亮著的「已儲存」,看久了等於沒有。 */
.qd-save{display:flex;align-items:center;gap:6px;
  padding:4px 10px;border:1px solid #ffffff1f;border-radius:999px;background:#20202cd9;
  color:#b9b9c8;font:11px system-ui;backdrop-filter:blur(6px);pointer-events:none;
  transition:opacity .3s}
.qd-save[hidden]{display:none}
.qd-save[data-state=saved]{opacity:.4}
.qd-save>i{width:7px;height:7px;border-radius:50%;background:#6b6b7a;flex:none}
.qd-save[data-state=saved]>i{background:#35c46a}
.qd-save[data-state=saving]>i{background:#e0a33a}
.qd-save[data-state=pending]>i{background:#8b8b9c}
.qd-save[data-state=error]>i{background:#d9534f}
/*
  新增元件。

  桌面上開東西的方式是「在桌布上按右鍵」，而那是一個**要先知道才用得到的入口**。
  觸控裝置根本沒有右鍵；就算長按觸發得了同一個事件，它仍然沒有任何提示。

  一度只在 pointer:coarse 的裝置上畫，理由是「滑鼠上右鍵是慣例」。那個判斷
  兩層都錯：模擬器與二合一筆電回報的是滑鼠，於是那些真的需要它的地方反而看不
  到；而更根本的是，**一個只有先知道才找得到的入口，在滑鼠上也一樣是不存在的**
  ——這個專案的其他每一處都在講「不在樹上的東西不存在」，介面不該是例外。

  所以永遠畫。安靜一點就好：半透明、貼著角落、滑鼠移過去才亮起來。
*/
.qd-pill{pointer-events:auto;max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
  padding:5px 14px;border:1px solid #ffffff26;border-radius:999px;background:#20202ce0;
  color:#dcdce8;font:12px system-ui;cursor:pointer;backdrop-filter:blur(6px)}
.qd-pill:hover{background:#2c2c3ce8;border-color:#ffffff40}
/* 釘住列表的按鈕跟 `+` 都是純圖示——flex:none 讓它們不參與收縮，dock 塞不
   下時該縮的是桌面名稱那顆膠囊（或 carousel viewport），不是這些圖示。 */
.qd-pill.qd-pinbtn{flex:none;max-width:none;font-size:12.5px}
/* 新增元件那顆——放在 dock 裡當一顆膠囊，跟釘住按鈕併排。字大一號、字重
   加粗，讓 `+` 在同一列膠囊裡一眼看得出「主要動作」是它。 */
.qd-pill.qd-addbtn{flex:none;padding:5px 12px;font:600 15px system-ui;line-height:1}
/* 說明按鈕。跟其他 dock 圖示同一種節奏（flex:none、不縮），字體不粗——
   它是次要動作，不該搶掉 `+` 的視覺重量。 */
.qd-pill.qd-helpbtn{flex:none;padding:5px 12px;font:600 13px system-ui;line-height:1}
/* 身分（帳號 · 桌面名）膠囊。是要讀的，不是要按的——所以不給 hover 也不給游標。
   除非它同時是「換一個桌面」那顆按鈕（qd-swap），那時候要看得出來按得下去。
   flex 讓它可縮，min-width 保底、太窄至少能看到縮寫；max-width 沿用 qd-pill 200px 截尾。 */
.qd-pill.qd-who{flex:0 1 auto;min-width:120px;cursor:default;opacity:.7;font-size:11.5px}
.qd-pill.qd-who.qd-swap{cursor:pointer;opacity:.85}
.qd-pill.qd-who.qd-swap:hover,.qd-pill.qd-who.qd-swap:active{opacity:1}
.qd-pill.qd-who.qd-warn{opacity:1;border-color:#f0c67480;color:#f6d99a}
.qd-pill.qd-pinbtn.qd-on{border-color:#ffd45e66;color:#ffe6a8}

/* 釘住清單：dock 那顆 📌 點開的東西。掛在 root 上而不是 dock 裡，dock 是
   pointer-events:none 的。 */
.qd-pinlist{position:absolute;z-index:4;min-width:190px;max-height:50vh;overflow:auto;
  padding:6px;border:1px solid #ffffff26;border-radius:10px;background:#1b1b26f2;
  box-shadow:0 12px 32px #0009;backdrop-filter:blur(8px);font:12px system-ui;color:#dcdce8}
/* 一列裡塞三件事——名字（flex:1 吃剩下）、鎖／解狀態、關掉按鈕。前兩件事併
   成一顆「切換鎖定」的按鈕，關掉是另外一顆——**鎖跟關是兩個動作**，混在同一
   個 tap 目標會誤觸。 */
.qd-pinrow{display:flex;width:100%;gap:6px;align-items:stretch;padding:0}
.qd-pinlock{flex:1;display:flex;gap:10px;align-items:center;justify-content:space-between;
  padding:6px 8px;border:0;border-radius:6px;background:none;color:inherit;font:inherit;
  cursor:pointer;text-align:left;min-width:0}
.qd-pinlock:hover{background:#ffffff14}
.qd-pinlock b{font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0}
.qd-pinlock span{flex:none;opacity:.55;font-size:11px}
.qd-pinlock.qd-on span{opacity:1;color:#ffd45e}
.qd-pinlist-none{padding:8px;opacity:.45}

/* 說明文件面板。dock 上「?」按鈕開的東西。跟釘住清單不同——那是一個小的
   下拉、這是要讀的內容，畫成右側 sheet：**桌面上一般人只從一邊看**，所以
   從邊緣進來、不遮到中間的元件。窄螢幕（手機）鋪滿整片。 */
.qd-help-backdrop{position:absolute;inset:0;z-index:4;background:#000000a0;
  opacity:0;pointer-events:none;transition:opacity .18s ease}
.qd-help-backdrop.qd-open{opacity:1;pointer-events:auto}
.qd-help{position:absolute;top:0;bottom:0;right:0;z-index:5;
  width:min(480px, 100%);background:#12121cf5;
  border-left:1px solid #ffffff1f;box-shadow:-12px 0 40px #000a;
  backdrop-filter:blur(10px);
  transform:translateX(100%);transition:transform .22s cubic-bezier(.2,.8,.2,1);
  display:flex;flex-direction:column;overflow:hidden}
.qd-help.qd-open{transform:translateX(0)}
.qd-help-head{flex:none;display:flex;align-items:center;justify-content:space-between;
  padding:14px 16px 12px;border-bottom:1px solid #ffffff14}
.qd-help-title{font:600 16px system-ui;color:#f2f2f7}
.qd-help-close{border:0;background:none;color:#b9b9c8;font:16px system-ui;
  cursor:pointer;padding:6px 10px;border-radius:8px}
.qd-help-close:hover{background:#ffffff14;color:#fff}
/* 內容區——可捲、safe-area 底避免 home indicator 蓋到最後一行。 */
.qd-help-body{flex:1;overflow-y:auto;padding:16px 18px calc(24px + env(safe-area-inset-bottom));
  color:#c8c8d4;font:14px/1.6 system-ui;-webkit-overflow-scrolling:touch}
.qd-help-section{margin-bottom:22px}
.qd-help-section-title{font:700 15px system-ui;color:#e8e8ef;margin-bottom:8px}
.qd-help-para{margin:0 0 8px 0}
.qd-help-list{margin:0;padding-left:0;list-style:none}
.qd-help-item{position:relative;padding-left:14px;margin-bottom:6px}
/* 自繪 bullet——outside marker 用「一顆對齊 baseline」需要精準 vertical-align，
   inside padded + 絕對定位 ::before 比較穩，跨字型高度不會漂。 */
.qd-help-item::before{content:"";position:absolute;left:2px;top:.7em;
  width:4px;height:4px;background:#8b8b9c;border-radius:50%}
.qd-help-bold{color:#f2f2f7;font-weight:600}

/* 釘住：標題列與縮放把手一起收起，視窗變成畫面上一塊安靜的東西。
   收起來只是別讓人白拖一場——真正的強制在核心，寫 rect 會被拒，agent 也一樣。
   釘與解都在 dock 的 📌 上，因為標題列會連同自己的按鈕一起消失。 */
.qd-win.qd-pinned .qd-title{display:none}
.qd-win.qd-pinned .qd-rz{display:none}

/* 因果連線：看得到「那個數字為什麼變了」 */
.qd-arcs{position:absolute;inset:0;pointer-events:none;z-index:1;overflow:visible}
.qd-arc{fill:none;stroke-width:2;stroke-linecap:round;
  filter:drop-shadow(0 0 4px rgba(0,0,0,.6))}
.qd-arc-ok{stroke:rgba(122,162,255,.95)}
.qd-arc-no{stroke:rgba(255,120,110,.95)}
.qd-dot-ok{fill:rgba(160,195,255,1)}
.qd-dot-no{fill:rgba(255,150,140,1)}

/* 版面輔助線：拖曳時看得到落點 */
.qd-guides{position:absolute;inset:0;pointer-events:none;z-index:2;
  opacity:0;transition:opacity .12s ease-out}
.qd-guides.qd-on{opacity:1}
.qd-slot{position:absolute}
.qd-slot > i{position:absolute;display:block;border:1px dashed rgba(232,232,239,.32);
  border-radius:6px;transition:background .1s,border-color .1s}
.qd-slot.qd-target > i{border-style:solid;border-color:rgba(122,162,255,.95);
  background:rgba(74,110,224,.16);box-shadow:0 0 0 1px rgba(122,162,255,.35) inset}

/* 把手要在說明／設定面板**之上**（那兩張是 z-index:6）。面板從標題列下緣鋪到
   視窗底部，蓋住的正好是八個把手所在的邊。它們只有幾像素寬，壓在面板上不礙事，
   而反過來——打開設定就不能調整視窗大小——才是真的礙事。 */
.qd-rz{position:absolute;z-index:7;touch-action:none}
.qd-rz-n{top:0;left:12px;right:12px;height:6px;cursor:ns-resize}
.qd-rz-s{bottom:0;left:12px;right:12px;height:6px;cursor:ns-resize}
.qd-rz-w{left:0;top:12px;bottom:12px;width:6px;cursor:ew-resize}
.qd-rz-e{right:0;top:12px;bottom:12px;width:6px;cursor:ew-resize}
.qd-rz-nw{top:0;left:0;width:12px;height:12px;cursor:nwse-resize}
.qd-rz-ne{top:0;right:0;width:12px;height:12px;cursor:nesw-resize}
.qd-rz-sw{bottom:0;left:0;width:12px;height:12px;cursor:nesw-resize}
.qd-rz-se{bottom:0;right:0;width:12px;height:12px;cursor:nwse-resize}

/* 觸控裝置：6px 的邊框手指抓不到 */
@media (pointer: coarse){
  .qd-rz-n,.qd-rz-s{height:16px}
  .qd-rz-w,.qd-rz-e{width:16px}
  .qd-rz-nw,.qd-rz-ne,.qd-rz-sw,.qd-rz-se{width:26px;height:26px}
  .qd-btn{width:28px;height:28px}
  .qd-title{padding:9px 8px}
}
`

/** 內建的版面板型。格子是 0–1 的比例，所以任何螢幕尺寸都適用。 */
const DEFAULT_LAYOUTS: LayoutTemplate[] = [
  {
    id: 'halves',
    title: '左右各半',
    gap: 8,
    cells: [
      { x: 0, y: 0, w: 0.5, h: 1 },
      { x: 0.5, y: 0, w: 0.5, h: 1 },
    ],
  },
  {
    id: 'thirds',
    title: '三欄',
    gap: 8,
    cells: [0, 1, 2].map((i) => ({ x: i / 3, y: 0, w: 1 / 3, h: 1 })),
  },
  {
    id: 'quarters',
    title: '四宮格',
    gap: 8,
    cells: [0, 1, 2, 3].map((i) => ({
      x: (i % 2) / 2,
      y: Math.floor(i / 2) / 2,
      w: 0.5,
      h: 0.5,
    })),
  },
  {
    id: 'grid3x3',
    title: '九宮格',
    gap: 8,
    cells: Array.from({ length: 9 }, (_, i) => ({
      x: (i % 3) / 3,
      y: Math.floor(i / 3) / 3,
      w: 1 / 3,
      h: 1 / 3,
    })),
  },
  {
    id: 'main-side',
    title: '主畫面 + 側欄',
    gap: 8,
    cells: [
      { x: 0, y: 0, w: 2 / 3, h: 1 },
      { x: 2 / 3, y: 0, w: 1 / 3, h: 0.5 },
      { x: 2 / 3, y: 0.5, w: 1 / 3, h: 0.5 },
    ],
  },
]

/**
 * dock 那顆桌面膠囊上，名字最多幾個字。
 *
 * 用**字數**而不是 CSS 的 `max-width` + `text-overflow`：後者量的是像素，於是同
 * 一個上限在中文與英文下會截在完全不同的地方，而這條列上還有別的按鈕在搶寬度。
 * 數字數讓「會佔多少」是可預期的。
 */
const CHIP_NAME_MAX = 8

/**
 * 太長就截短加省略號。
 *
 * 用 `Array.from` 逐字拆，不用 `slice`：`slice` 切的是 UTF-16 碼元，遇到 emoji
 * 或任何 BMP 以外的字會把一個字剖成兩半，畫出來是一個問號方框——比截短難看得多。
 */
function ellipsis(text: string, max: number): string {
  const chars = Array.from(text)
  return chars.length > max ? `${chars.slice(0, max).join('')}…` : text
}

/**
 * 兩指的中點與間距——一次手勢裡唯一需要的兩個數字。
 *
 * 中點動了就是搬，間距變了就是縮放，兩件事在同一次手勢裡同時發生，人也是這樣
 * 用的。
 */
export interface Pinch {
  cx: number
  cy: number
  /** 兩指的直線距離 */
  d: number
  /** 兩指在各軸上分別張開多少。逐軸縮放要的是這兩個，不是 d。 */
  dx: number
  dy: number
}

export function pinchOf(points: readonly { x: number; y: number }[]): Pinch {
  const a = points[0] ?? { x: 0, y: 0 }
  const b = points[1] ?? a
  return {
    cx: (a.x + b.x) / 2,
    cy: (a.y + b.y) / 2,
    d: Math.hypot(b.x - a.x, b.y - a.y),
    dx: Math.abs(b.x - a.x),
    dy: Math.abs(b.y - a.y),
  }
}

/** 一個軸上張開不到這麼多像素，那個軸的比例就不可靠——兩個很近的數字相除會亂跳。 */
const PINCH_MIN_SPREAD = 24

/**
 * 某一個軸的縮放倍率。
 *
 * **逐軸算，不是用兩指的直線距離。** 用單一個距離比的話，兩指往左右拉開會把視
 * 窗的**高度**也一起放大——手指往橫的動卻長高，那就是「比例不對」。手機上更明
 * 顯：D39 讓核心接管 `x` 與 `w`，所以寬度那一半根本不會動，看到的只剩「橫向拉
 * 開 → 變高」。
 *
 * 那個軸上張得太開才可靠；兩指幾乎垂直排列時橫向只差幾像素，相除會亂跳，這時退
 * 回整體距離比。
 */
function axisScale(from: number, now: number, fromD: number, nowD: number): number {
  const raw = from >= PINCH_MIN_SPREAD ? now / from : fromD > 0 ? nowD / fromD : 1
  return Math.abs(raw - 1) < PINCH_DEADZONE ? 1 : raw
}

/**
 * 間距要變超過這個比例才算在縮放。
 *
 * 兩根手指拖著走的時候**一定會抖**，而沒有這個死區的話，每一次搬動都會順便把
 * 視窗縮個幾像素——那看起來像元件在呼吸。
 */
const PINCH_DEADZONE = 0.06

/**
 * 一次兩指手勢之後的視窗矩形。
 *
 * `start` 是手指按下那一刻的矩形，`from` / `now` 是那時與現在的中點與間距。全部
 * 對著**按下那一刻**算，不是逐格累加：累加會把每一次的取整誤差存起來，一次長距
 * 離的拖曳結束時視窗會偏掉，而那種偏移查不出來源。
 *
 * 縮放繞著視窗中心做，中心再跟著兩指的中點移動——所以捏的同時也搬得動，那正是
 * 手勢本來的樣子。
 */
export function pinchRect(start: WindowRect, from: Pinch, now: Pinch): WindowRect {
  const w = Math.max(MIN_W, Math.round(start.w * axisScale(from.dx, now.dx, from.d, now.d)))
  const h = Math.max(MIN_H, Math.round(start.h * axisScale(from.dy, now.dy, from.d, now.d)))
  const cx = start.x + start.w / 2 + (now.cx - from.cx)
  const cy = start.y + start.h / 2 + (now.cy - from.cy)
  return { x: Math.round(cx - w / 2), y: Math.round(cy - h / 2), w, h }
}

/** 使用者拖出來的視窗至少要能看見標題列與一點內容 */
const MIN_W = 160
const MIN_H = 90
const RESIZE_DIRECTIONS = ['n', 's', 'w', 'e', 'nw', 'ne', 'sw', 'se'] as const

/**
 * 這一次尺寸變化是**裝置轉向**，還是只是視窗被拉了一下。
 *
 * 判準只有一條：長寬比翻面，而且兩邊都不是近正方形。
 *
 * **比的是安定之後的尺寸**（見 `onCanvasResized` 的節流），不是每一次
 * ResizeObserver。這一點是必要的，不是最佳化：WebKit 轉向時是**分幾步**改尺寸
 * 的，中間會經過 `393×800 → 393×340 → 852×340` 這種形狀。逐格去比的話，第一
 * 格翻了面但尺寸差很遠、第二格根本沒翻面——**兩格都不算，於是整次轉向被漏掉**。
 * 這正是第一版在真機上什麼都沒發生的原因。
 *
 * 之前還多要求「新舊尺寸大致對調」，現在拿掉了：它擋的是桌機上把視窗拉窄那種
 * 誤判，而那件事已經由 `Shell.rotatable`（粗指標）擋在外面，留著只是讓判準多
 * 一個會在真機上失手的條件。
 *
 * 也不用 `screen.orientation`：那個 API 的 change 事件與 resize 誰先誰後各家不
 * 一樣，排錯順序的後果是轉向被整個漏掉——而那種漏掉只在真的裝置上才看得到。
 */
/**
 * 兩邊差不到這個比例就當它是正方形的。近正方形的畫布上任何一點抖動都會翻面，
 * 那裡的判準沒有分辨力，只能整段放棄。真的裝置離這個範圍很遠：手機兩邊差一半
 * 以上，平板也有三成。
 */
const ROTATION_MIN_ASPECT = 0.2

/** 尺寸靜止這麼久就當它安定了。轉向的那幾格變化全部落在這個窗口裡。 */
const ROTATION_SETTLE_MS = 250

/**
 * 轉向之後的矩形：**整個轉置**——x 與 y 對調，w 與 h 也對調。
 *
 * 直式由上到下的 A B C D，轉完就是由左到右的 A B C D，順序保留。
 *
 * ## 為什麼不是真的旋轉
 *
 * 真正的九十度旋轉會**反轉一個軸**，最上面的會跑到最右邊，變成 D C B A。那在
 * 幾何上無可挑剔，在一面牆上卻是錯的：那幾個東西有順序，不該因為把裝置轉個方
 * 向就顛倒。轉置另外解掉三件旋轉帶來的麻煩——完全可逆（轉兩次就是原狀，一個像
 * 素不差）、跟往哪邊轉無關（不必分辨順時針逆時針）、不會算出負座標（而
 * `.qd-scroll` 是 `overflow:auto`，**CSS 捲不到負座標**，那等於視窗消失）。
 *
 * ## 為什麼寬高也要換
 *
 * 一開始只換 x/y、保留寬高，因為那樣每個 iframe 的像素尺寸不變、沒有元件需要
 * 重排。但那在真的版面上不成立：手機單欄的視窗是 377 寬、垂直間距 272，橫著排
 * 的時候 377 > 272，**必定重疊**。間距是由原本的高度決定的，所以能不能並排也
 * 只能由高度決定——寬高一起換，377×200 變成 200×377，200 < 272 就不疊了，而且
 * 377 高剛好放得進 393 的橫式畫布。
 *
 * 代價是每個 iframe 的尺寸真的變了，元件會重排一次。那本來就是要的：一個直的
 * 窗變成橫的窗，裡面的東西本來就該跟著換一種排法（`.qd-win` 有
 * `container-type:inline-size`，容器查詢自己會處理）。
 */
export function transposeRect(rect: WindowRect): WindowRect {
  return { x: rect.y, y: rect.x, w: rect.h, h: rect.w }
}

export function isRotation(
  prev: { w: number; h: number },
  next: { w: number; h: number },
): boolean {
  if (prev.w <= 0 || prev.h <= 0 || next.w <= 0 || next.h <= 0) return false
  if (prev.w > prev.h === next.w > next.h) return false
  return squarish(prev) === false && squarish(next) === false
}

function squarish(size: { w: number; h: number }): boolean {
  const long = Math.max(size.w, size.h)
  return (long - Math.min(size.w, size.h)) / long < ROTATION_MIN_ASPECT
}

/**
 * `"390x844"` → `{w:390,h:844}`。看不懂的一律當成 null（＝跟著外面那一層）。
 *
 * 上下限不是為了防誰，是為了讓打錯的人看得出來：一個 `39000x844` 會做出一塊捲
 * 不完的畫布，而那看起來像壞掉，不像打錯。節點上的 pattern 擋掉了大部分，這裡
 * 是最後一道——值也可能是別的地方寫進來的。
 */
function parseSize(spec: unknown): { w: number; h: number } | null {
  if (typeof spec !== 'string') return null
  const m = /^(\d{2,5})x(\d{2,5})$/.exec(spec.trim())
  if (!m) return null
  const w = Number(m[1])
  const h = Number(m[2])
  if (w < 100 || h < 100 || w > 20000 || h > 20000) return null
  return { w, h }
}

/**
 * shell 認得的能力名稱。
 *
 * 一份白名單，不是照單全收：目錄裡的字串會變成 iframe 的 `allow` 屬性，而一個
 * 不在這裡的名字要嘛是打錯（靜靜地沒有作用），要嘛是有人想塞別的東西進那個屬
 * 性。兩種都該被擋下來並說出來。
 *
 * 這份清單刻意短。要多一個能力，就是有人想清楚了為什麼——那正是它該有的成本。
 */
const KNOWN_PERMISSIONS: readonly string[] = [
  'microphone',
  'camera',
  'geolocation',
  'display-capture',
  'clipboard-read',
  'clipboard-write',
  'fullscreen',
  'autoplay',
  'midi',
]

/**
 * 一個目錄項目實際會拿到的 `allow` 字串。
 *
 * 抽出來是為了測得到：這是一條安全界線——「存檔給不了權限」「不認得的名字不會
 * 進到那個屬性裡」——而界線該有一個不必開瀏覽器就跑得起來的證明。
 */
export function grantedPermissions(
  entry: { permissions?: readonly string[] } | undefined,
  onUnknown?: (name: string) => void,
): string {
  const granted: string[] = []
  for (const name of entry?.permissions ?? []) {
    if (KNOWN_PERMISSIONS.includes(name)) granted.push(name)
    else onUnknown?.(name)
  }
  return granted.join('; ')
}

export class Shell {
  private readonly bus: Bus
  private readonly root: HTMLElement
  /**
   * 所有視窗住的那一層。
   *
   * 它存在只為了一件事：**建立堆疊脈絡**。`$window/z` 是一個誰都寫得動的整數，
   * agent 寫個 2147483647 進去也完全合法——只要視窗和底部膠囊是同一層的兄弟，
   * 那就是一場沒有終點的數字比大小，而膠囊是釘住之後唯一還碰得到的表面，輸不起。
   * 包進一層有 z-index 的容器之後，裡面的數字再大也只在裡面排序。
   */
  private readonly layer: HTMLDivElement
  private readonly store: SessionStore | null
  private readonly account: Account | null
  private readonly onSwitchDesktop: (() => void) | null
  private readonly showHelp: boolean
  private readonly catalog: CatalogEntry[]
  private readonly windows = new Map<string, WindowView>()
  private dock: HTMLDivElement | null = null
  private dockHideTimer: ReturnType<typeof setTimeout> | null = null
  /** 使用者拖過的 dock 位置（螢幕座標，相對於 `screen`）。null = 貼底置中。
   *  存 localStorage 於 `qd:dock:pos`，boot 時讀進來。 */
  private dockPos: { x: number; y: number } | null = null
  /** dock 是不是直式。存 localStorage 於 `qd:dock:vertical`。直式必為浮動。 */
  private dockVertical: boolean = false
  private pinList: HTMLDivElement | null = null
  private pinListScrim: HTMLDivElement | null = null
  private pinListOff: (() => void) | null = null
  private helpPanel: HTMLDivElement | null = null
  private helpBackdrop: HTMLDivElement | null = null
  private helpOff: (() => void) | null = null
  private menu: HTMLDivElement | null = null
  private menuScrim: HTMLDivElement | null = null
  private sessionApp: Registration | null = null
  private topZ = 0
  private arcs: SVGSVGElement | null = null
  private nextArcId = 1
  private guides: HTMLDivElement | null = null
  private readonly slots: HTMLElement[] = []
  private guideTimer: ReturnType<typeof setTimeout> | null = null
  /** 上一次安定下來的畫布尺寸。轉向時它就是「舊座標系」。 */
  private lastView = { w: 0, h: 0 }
  /**
   * 上一次安定時的螢幕方向（`screen.orientation.type`）。用來分辨「裝置轉了」
   * 與「視窗被拉了一下」。
   *
   * **一出生就要帶著當下的值。** 初值給 `''` 的話，開機那一次安定會看到「空字
   * 串 → portrait-primary」而判定成一次轉向，於是把釘住的螢幕尺寸對調——一塊
   * 852 寬的畫布擺在 393 寬的直式視窗裡，`overflow:hidden` 之後看得到的是一塊
   * 393×393 的方形。而且它每次開機都做一次，那個值會在兩個方向之間來回翻，還
   * 會被存進 session。
   *
   * 欄位初始化在建構式本體之前跑，所以第一次 `onCanvasResized()` 時它已經有值。
   */
  private lastOrientation: string = globalThis.screen?.orientation?.type ?? ''
  /**
   * 這台機器會不會被拿起來轉。**只用在開機那一次的校正**，不是整個功能的總開
   * 關——上一版拿它當總開關，猜錯的代價是轉向完全不動而且悄無聲息。用在這裡風
   * 險是對的：猜錯只是少做一次校正，轉向照樣運作。
   */
  private readonly rotatable =
    typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches
  private settleTimer: ReturnType<typeof setTimeout> | null = null
  /** 進行中的兩指手勢：按下那一刻的矩形與兩指狀態，逐 uid。 */
  private readonly gestures = new Map<
    string,
    { rect: WindowRect; from: Pinch; queued: WindowRect | null }
  >()
  private saveTimer: ReturnType<typeof setTimeout> | null = null
  private saveDeadline: number | null = null
  private restoring = false
  private savingSuppressed = false
  /**
   * 正在飛的那一次存檔。
   *
   * 存檔是非同步的,而 flushSave 一開始就把 timer 清掉了——所以在它 await 的期
   * 間,新的 scheduleSave 會再排一個,而那個可能在前一次回來之前就觸發。兩次存
   * 檔於是同時在飛,**帶著同一個 rev**:伺服器接受先到的那一個,後到的必然 409。
   *
   * 那個 409 不是「別的地方改過」,是自己撞自己。而它會一直發生,因為觸發存檔的
   * 東西還在動。
   */
  private saving: Promise<void> | null = null
  /** 飛行途中又有變動。等這一次落地再存一次,而不是插隊。 */
  private saveAgain = false
  private screen!: HTMLDivElement
  private scroll!: HTMLDivElement
  private canvas!: HTMLDivElement
  private statusBar: HTMLDivElement | null = null
  private link: HTMLDivElement | null = null
  private saveBadge: HTMLDivElement | null = null
  /** 選單收起來的時候要解掉的訂閱。留著的話每開一次就多一個。 */
  private menuCleanup: (() => void) | null = null
  /** 存檔現在的處境。'' 表示這台桌面沒有存檔可言,那就什麼都不顯示。 */
  /**
   * 存檔現在的處境。'' 表示這台桌面沒有存檔可言,那就什麼都不顯示。
   *
   * **沒有「未儲存」這一種。** 有過:排程到寫入之間顯示它。但那一段通常不到兩
   * 秒,於是它變成一個每隔一陣子閃一下的字——而一個會閃的狀態燈,教人的是忽略
   * 它。真正要說的只有兩件事:正在寫(所以還沒落地),以及沒有同步上去。其餘時
   * 候就是已儲存。
   */
  private saveState: '' | 'saved' | 'saving' | 'error' = ''
  private saveError = ''
  /**
   * 上一次真的送出去的那一份，去掉時間戳之後的樣子。
   *
   * 存在的理由是：**「什麼都沒變」不是一次存檔。** 任何一條會 touch 的 topic
   * 只要重覆發布同一個值，就會排一次存檔，而那一次會把 rev 往前推、把畫面停在
   * 「未儲存」，然後下一次重覆又來一遍。這裡不去追是誰在重覆——比對結果比追來
   * 源可靠，而且它對每一個未來的來源都成立。
   */
  private lastSaved = ''
  /**
   * 最後一次是哪一條 topic 叫它存的。
   *
   * 純粹為了查:「我什麼都沒動,它每隔幾秒存一次」這種問題,沒有這一行就得一條
   * 一條猜。滑過左上角那顆膠囊就看得到,不必開開發者工具——而需要開工具才問得
   * 出來的答案,實際上等於問不出來。
   */
  private saveReason = ''
  private linkState = ''
  private locale: Locale = 'zh-Hant'
  /**
   * 查表。每次語言變了就換一個新的——把它當成不可變的值傳來傳去，比讓每個
   * 呼叫端記得帶語言參數少一整類錯。
   */
  private t = translator('zh-Hant')
  private readLimit: () => number = () => -1
  /** 系統 app 的網址。它們不算在元件上限裡。 */
  private systemUrls = new Set<string>()

  /**
   * 還能再開幾個元件。null 表示不限。
   *
   * 用網址判斷是不是系統 app,跟開機時去重用的是同一個依據(D5:app 就是一個
   * 網址)——還原回來的那一份帶著隨機 uid,只看 uid 會把它算成使用者的元件。
   */
  /**
   * 換語言之後把畫面重貼一次。
   *
   * 圖示按鈕的提示文字存在 data-i18n-title 裡，掃一遍就好——不然每個視窗要多存
   * 七個 DOM 參照，只為了改一行 tooltip。其餘（dock、選單）本來就有自己的重繪
   * 函式，叫一次比自己記得改哪幾個節點可靠。
   *
   * 開著的設定面板不重畫:它裡面可能有還沒送出的輸入。換語言把人打到一半的字
   * 洗掉，比那個面板暫時留著舊語言糟得多。
   */
  private applyLocale(): void {
    for (const el of this.root.querySelectorAll<HTMLElement>('[data-i18n-title]')) {
      const key = el.dataset['i18nTitle'] as ShellKey | undefined
      if (key) el.title = this.t(key)
    }
    this.renderDock()
    if (this.link) this.renderLink()
    if (this.saveBadge) this.renderSave()
    // 說明面板的內容是在開啟時定住的（buildOnce），懶得逐節重畫——關掉就好，
    // 使用者要看再開一次會拿到新語言的版本。
    if (this.helpPanel) this.closeHelp()

    // 然後把每個 app 重載一次。
    //
    // 它們的 title / summary / groups 是在 register() 當下進 $meta 的，而核心不
    // 接受重複註冊——所以一個 app 的視窗標題在它這一輩子是定的。不重載的話畫面
    // 會換一半:按鈕的提示變了、視窗標題沒變，而那看起來像壞掉。
    //
    // 重載是安全的:app 是 topic 之上的視圖，設定都在匯流排裡(而且 rw 的還在
    // 存檔裡)，回來就照新語言再畫一次。代價是打到一半、還沒送出的輸入會沒了
    // ——換語言是罕見而且刻意的動作，整片一起換比換一半好。
    // 重設 src,不是 contentWindow.location.reload()——app 在另一個來源上,碰它
    // 的 location 會直接丟 SecurityError。這也是那顆「重新載入」按鈕的做法。
    for (const view of this.windows.values()) {
      view.iframe.src = this.frameUrl(view.url)
    }
  }

  private windowRoom(): number | null {
    const limit = this.readLimit()
    if (!Number.isFinite(limit) || limit < 0) return null
    let widgets = 0
    for (const view of this.windows.values()) {
      if (!this.systemUrls.has(view.url)) widgets++
    }
    return limit - widgets
  }

  private constructor(options: ShellOptions) {
    this.bus = options.bus
    this.root = options.root
    this.store = options.session ?? null
    this.account = options.account ?? null
    this.onSwitchDesktop = options.onSwitchDesktop ?? null
    this.showHelp = options.showHelp ?? true
    // 讀 dock 位置 / 方向偏好——**每張桌面自己存**（見 `dockPosKey` /
    // `dockVerticalKey`）。原因很直觀：不同桌面上面擺的元件不一樣，dock 的
    // 好位置常常不一樣；共用一份的話換一張桌面 dock 就跑到擋內容的位置。
    // localStorage 在 sandbox iframe / cookies 禁用時會丟；讀不到就退回
    // 預設，寫不進去也不影響這一次使用（下次重整不記得，就這樣）。
    try {
      const savedPos = localStorage.getItem(this.dockPosKey())
      if (savedPos) {
        const parsed = JSON.parse(savedPos) as unknown
        // `typeof NaN === 'number'` 為真——早期版本或損壞的存檔可能塞進 NaN，
        // 過型別檢查但接下來 `${NaN}px` 會被 CSS 無視，dock 就 render 到 (0,0)
        // 蓋住左上角的連線膠囊。用 Number.isFinite 一併擋掉。
        if (
          parsed && typeof parsed === 'object'
          && Number.isFinite((parsed as { x: unknown }).x as number)
          && Number.isFinite((parsed as { y: unknown }).y as number)
        ) {
          this.dockPos = { x: (parsed as { x: number }).x, y: (parsed as { y: number }).y }
        } else {
          // 損壞資料清掉，下次啟動不再撿到。
          try { localStorage.removeItem(this.dockPosKey()) } catch {}
        }
      }
    } catch {
      try { localStorage.removeItem(this.dockPosKey()) } catch {}
    }
    try {
      this.dockVertical = localStorage.getItem(this.dockVerticalKey()) === 'true'
    } catch {}
    this.catalog = [...(options.catalog ?? [])]
    this.readLimit =
      typeof options.windowLimit === 'function'
        ? options.windowLimit
        : () => (typeof options.windowLimit === 'number' ? options.windowLimit : -1)
    this.systemUrls = new Set((options.systemApps ?? []).map((spec) => spec.url))
    this.root.classList.add('qd-root')

    const style = document.createElement('style')
    style.textContent = STYLE
    document.head.append(style)

    this.bus.setCatalog(this.catalog)
    this.bus.setLayouts(options.layouts ?? DEFAULT_LAYOUTS)

    void this.bus.subscribe('$sys/screen', (message) => {
      if (message.type === 'state') this.applyScreen(message.value)
    })

    if (options.statusTopic) {
      void this.bus.subscribe(options.statusTopic, (message) => {
        if (message.type !== 'state') return
        this.linkState = String(message.value ?? '')
        this.renderLink()
      })
    }

    // 語言活在 topic 上，不在建構參數裡。所以「換語言」跟任何其他設定一樣，是
    // 一次寫入——人從設定面板改、agent 也改得動，兩條路是同一條。
    void this.bus.subscribe('$sys/locale', (message) => {
      if (message.type !== 'state' || !isLocale(message.value)) return
      if (message.value === this.locale) return
      this.locale = message.value
      this.t = translator(this.locale)
      this.applyLocale()
    })
    // $sys/cmd/launch 交給宿主實作。核心只負責查目錄，不知道 iframe 是什麼。
    this.bus.setLauncher((entry, placement) => {
      // 只擋這裡。還原與系統 app 走 launch() 直接進來,不經過這條路——那是刻意的:
      // 這是「有人（或 agent）現在要再開一個」的唯一入口,而上限管的正是那件事。
      const room = this.windowRoom()
      if (room !== null && room <= 0) {
        // 丟出去會被核心包成 E_REJECTED 並附上這句話,所以人看得到、agent 也讀
        // 得到——它會知道要先關掉一個,而不是重試到逾時。
        throw new Error(
          `這個桌面最多放 ${this.readLimit()} 個元件,已經滿了。關掉一個再開,或升級方案。`,
        )
      }
      return { uid: this.launchFromCatalog(entry, placement) }
    })

    // 三層，而不是兩層。
    //
    //   root    掛載點，就是瀏覽器視窗。它只負責把螢幕擺中間。
    //   screen  螢幕。桌布、dock、連線燈、選單、面板都在這裡，釘住不動。
    //   canvas  桌面。視窗住在這裡，座標也是這裡的座標；比螢幕大就捲動。
    //
    // 分成兩個尺寸是因為它們本來就是兩件事：**螢幕是看得見多少，桌面是有多少
    // 可以看**。手機的玻璃很窄，不代表你的桌面只能那麼窄——它可以又長又寬，捲
    // 過去就是。混成一個尺寸的時候，「窄螢幕」與「小桌面」被迫是同一件事。
    this.screen = document.createElement('div')
    this.screen.className = 'qd-screen'
    this.root.append(this.screen)

    this.scroll = document.createElement('div')
    this.scroll.className = 'qd-scroll'
    this.canvas = document.createElement('div')
    this.canvas.className = 'qd-canvas'
    this.scroll.append(this.canvas)
    this.screen.append(this.scroll)

    /*
      捲動指示器：只在捲的時候出現，停下來就淡出。

      原生捲軸藏起來之後，「這裡還有東西」就沒有人說了——而這塊桌面本來就會比
      螢幕高（視窗排到螢幕外時畫布自己會長），不說的話那些視窗等於不存在。

      它是螢幕的子元素而不是捲動層的，所以不佔版面、不跟著捲、也不會再引發那個
      「捲軸出現→畫布變窄」的迴圈。
    */
    const hintY = document.createElement('div')
    hintY.className = 'qd-scrollhint'
    const hintX = document.createElement('div')
    hintX.className = 'qd-scrollhint qd-scrollhint-x'
    this.screen.append(hintY, hintX)

    let fade: ReturnType<typeof setTimeout> | undefined
    this.scroll.addEventListener(
      'scroll',
      () => {
        const box = this.scroll
        // +1 是留給次像素的：畫布與螢幕一樣高的時候，兩邊常常差 0.x 個像素，
        // 而那會讓一條指示器在完全捲不動的畫面上閃一下。
        const canY = box.scrollHeight > box.clientHeight + 1
        const canX = box.scrollWidth > box.clientWidth + 1
        if (canY) {
          const h = Math.max(24, (box.clientHeight / box.scrollHeight) * box.clientHeight)
          hintY.style.height = `${h}px`
          const travel = box.scrollHeight - box.clientHeight
          hintY.style.transform = `translateY(${(box.scrollTop / travel) * (box.clientHeight - h)}px)`
        }
        if (canX) {
          const w = Math.max(24, (box.clientWidth / box.scrollWidth) * box.clientWidth)
          hintX.style.width = `${w}px`
          const travel = box.scrollWidth - box.clientWidth
          hintX.style.transform = `translateX(${(box.scrollLeft / travel) * (box.clientWidth - w)}px)`
        }
        hintY.style.opacity = canY ? '1' : '0'
        hintX.style.opacity = canX ? '1' : '0'
        clearTimeout(fade)
        fade = setTimeout(() => {
          hintY.style.opacity = '0'
          hintX.style.opacity = '0'
        }, 700)
      },
      { passive: true },
    )

    this.layer = document.createElement('div')
    this.layer.className = 'qd-layer'
    this.canvas.append(this.layer)

    this.arcs = document.createElementNS(SVG_NS, 'svg')
    this.arcs.setAttribute('class', 'qd-arcs')
    this.canvas.append(this.arcs)

    this.guides = document.createElement('div')
    this.guides.className = 'qd-guides'
    this.canvas.append(this.guides)

    this.onCanvasResized()
    // 桌面尺寸變了，格子的像素換算也跟著變——所以要持續回報
    new ResizeObserver(() => this.onCanvasResized()).observe(this.canvas)
    /*
      轉向的第二個觸發來源。

      **釘住螢幕尺寸的桌面上，上面那個 ResizeObserver 一次都不會響**：`$sys/screen`
      給定之後 `.qd-screen` 是固定像素，`.qd-canvas` 是它的 100%，所以裝置轉了畫布
      也不會變。而手機上的桌面幾乎都是釘住的——SOP 第 8 節開頭就叫代理人先讀
      `$sys/screen`，桌面小幫手擺元件時就會設它。

      少了這一條，轉向在最常見的那種桌面上完全不會發生，而症狀是「什麼都沒動」，
      跟沒部署一模一樣。
    */
    const onTurn = (): void => this.scheduleSettle()
    globalThis.screen?.orientation?.addEventListener('change', onTurn)
    addEventListener('orientationchange', onTurn)

    // 看不見的時候瀏覽器會節流計時器。把這件事變成 topic 上的事實，
    // app 與 agent 才知道現在不能依賴輪詢。
    const reportVisible = (): void => this.bus.setVisible(!document.hidden)
    reportVisible()
    document.addEventListener('visibilitychange', reportVisible)

    this.wireTopics()
    this.wireFocusTracking()
    this.wireHostDragAPI()
    this.mountSessionApp()
  }

  /**
   * 回報的是**畫布**,不是瀏覽器視窗。
   *
   * 因為畫布就是設計面:版面板型、$sys/cmd/snap、元件的容器查詢都該對著它算。
   * 設成 390×844 的時候,桌面**真的**是 390 寬——所以那不是模擬,那些東西一個
   * 都不用改就會跟著對。
   */
  private reportViewport(): void {
    this.bus.setViewport({ w: this.canvas.clientWidth, h: this.canvas.clientHeight })
  }

  /**
   * 畫布尺寸變了。回報出去，順便看看這一次是不是轉向。
   *
   * 回報一定在重排之前：`$sys/cmd/snap` 讀的是 `$sys/viewport`，先重排的話它
   * 會拿舊尺寸去算格子。
   */
  private onCanvasResized(): void {
    // viewport 立刻回報，不等節流：格子的像素換算、元件的容器查詢都靠它，慢一
    // 拍會讓中間那幾格畫錯。要等的只有「這次算不算轉向」。
    this.reportViewport()
    this.scheduleSettle()
  }

  private scheduleSettle(): void {
    if (this.settleTimer !== null) clearTimeout(this.settleTimer)
    this.settleTimer = setTimeout(() => this.settleView(), ROTATION_SETTLE_MS)
  }

  /** 尺寸不再變動了，才回頭看這一連串變化整體上是不是一次轉向。 */
  private settleView(): void {
    this.settleTimer = null
    const orientationNow: string = globalThis.screen?.orientation?.type ?? ''
    const didTurn = orientationNow !== this.lastOrientation
    const pinned = parseSize(this.bus.read('$sys/screen')?.value)
    if (pinned) {
      this.lastOrientation = orientationNow
      this.settlePinnedScreen(pinned, didTurn)
      return
    }

    const next = { w: this.canvas.clientWidth, h: this.canvas.clientHeight }
    const prev = this.lastView
    this.lastView = next

    /*
      螢幕方向真的變了嗎。

      這一條取代了原本的「粗指標才算」。那個守門是拿一個猜的環境判斷去當總開
      關，而風險完全不對稱：`(pointer: coarse)` 在某個 WebView 裡是 false 的
      話，整段轉向一次都不會跑，症狀是「什麼都沒發生」——最難查的那一種，因為
      看起來跟沒部署一模一樣。

      問方向本身直接得多，而且在有節流之後是安全的：等到尺寸靜止 250ms 才比，
      `screen.orientation` 早就更新完了。原本不敢用它是怕 change 事件與 resize
      的先後順序各家不一樣，而節流把那個問題整個繞開——這裡讀的是**當下的值**，
      不是等一個事件。

      桌機因此仍然擋得住：把瀏覽器視窗拉高拉窄會讓畫布翻面，但**螢幕沒有轉**，
      type 不變，所以不算。

      認不得這個 API 的環境就只看幾何——寧可在一個沒有人會轉的機器上多做一次，
      也不要在會轉的機器上完全不動。
    */
    const orientation: string = globalThis.screen?.orientation?.type ?? ''
    const turned = orientation !== this.lastOrientation
    this.lastOrientation = orientation
    if (orientation !== '' && !turned) return

    if (isRotation(prev, next)) {
      void this.withoutSaving(() => this.reflowAfterRotation(prev, next))
    }
  }

  /**
   * 釘住尺寸的螢幕轉向了：把那個尺寸也對調，再重擺一次。
   *
   * 這種桌面上畫布不會跟著裝置變，所以**沒有幾何可以判斷**——只能問方向。回傳
   * true 表示這一輪由這裡處理掉了。
   *
   * 為什麼要對調而不是放著不動：`$sys/screen` 在手機上的意思就是「這片玻璃有多
   * 大」（SOP §8），而玻璃轉了，長寬本來就換了位置。放著不動的話畫面上會是一塊
   * 393 寬的卡片被 `margin:auto` 置中在 852 寬的視窗裡，左右兩條空的——那不是一
   * 個決定，那只是沒有人處理過轉向。
   *
   * 它是一次真的寫入，所以會進存檔、也會顯示在桌面設定裡。轉回去會再換回來，兩
   * 個方向對稱。
   *
   * 順帶會跨過 D39 那條線：`852` 寬大於 520，單欄強制解除——橫式本來就放得下兩
   * 欄。轉回直式時再套回來，而 `rect` 的部分寫入會讓 `w` 自動回到那一欄的寬度。
   */
  private settlePinnedScreen(pinned: { w: number; h: number }, turned: boolean): boolean {
    this.lastView = pinned
    if (squarish(pinned)) return true

    /*
      目標很單純：**釘住的尺寸要跟裝置目前的方向一致**。

      不是「轉了就對調」。那樣只在轉向的當下對，而存檔裡那個尺寸是會跟著走的
      ——在橫式關掉 app、回到直式再打開，存檔說 852×393，裝置是直的，桌面就是
      一塊躺著的方框。對調一次只會讓它在另一個方向錯。

      比的是實際可用空間而不是 `screen.orientation`：後者在缺這個 API 的環境是
      空的，而「現在比較寬還是比較高」到處都問得到。

      `rotatable` 只守開機那一次的校正：在電腦上把螢幕釘成 390×844 來排手機版
      面是正當用法，那塊玻璃不該因為瀏覽器視窗比較寬就被轉過去。真的轉向
      （`turned`）不受這道守門影響——那時裝置確實轉了。
    */
    const deviceLandscape = innerWidth > innerHeight
    if (pinned.w > pinned.h === deviceLandscape) return true
    if (!turned && !this.rotatable) return true

    const swapped = { w: pinned.h, h: pinned.w }
    this.lastView = swapped
    // 先換螢幕再重擺：板型那條路會呼叫 $sys/cmd/snap，而它讀的是 $sys/viewport，
    // 得先是新的尺寸才算得對。
    void this.withoutSaving(async () => {
      await this.bus.write('$sys/screen', `${swapped.w}x${swapped.h}`, { origin: 'shell' })
      await this.reflowAfterRotation(pinned, swapped)
    })
    return true
  }

  /**
   * 轉向之後把視窗重新擺一次。
   *
   * ## 為什麼要有這件事
   *
   * 視窗座標是絕對像素，所以裝置一轉，每一個視窗都還停在原本的位置——一份直
   * 式排好的版面轉成橫式之後，是一疊擠在左邊三分之一、下面全空的東西。桌面本
   * 來就沒有「轉向」這個概念，因為在這之前它只活在一塊不會轉的玻璃上。
   *
   * ## 兩種重排，看有沒有選板型
   *
   * **選了板型**：重新吸附。格子是 0–1 的比例（D21），本來就在任何尺寸下都成
   * 立，所以這是最好的答案——它連長寬比變了都對。要用**轉向前**的尺寸決定原本
   * 在哪一格：手上這個矩形是舊座標系裡的數字，拿新尺寸去比會挑到錯的格子。
   *
   * **自由版面**：轉位置，不轉框。視窗的中心繞著畫布轉九十度，`w`/`h` 原封不
   * 動——一疊直的變成一排橫的，而**每個 iframe 的像素尺寸完全沒變，所以沒有任
   * 何一支元件需要重新排版**。板型那條會改尺寸，二十幾支元件每一支都得再跑一
   * 次版面；這條一次都不用。
   *
   * ## 釘住的不動
   *
   * 核心對釘住的視窗一律拒絕 `$window/rect`，**不分來源**（D20：「只在 shell
   * 那邊藏起拖曳把手的話，agent 照樣搬得走——那不是鎖，是裝飾」）。這裡直接跳
   * 過它們，而不是寫下去收一個 `E_DENIED`：那個錯誤會進時間軸，而它不是錯——
   * 是釘住這件事正在生效。**「別動」包含轉向也不動。**
   *
   * 代價是其他東西會繞著它轉，看起來突兀。那是釘住本來的意思，不是這裡要修的
   * 東西——要它跟著轉就先解鎖。
   *
   * ## 轉完放不下的
   *
   * 直式 377×600 的視窗轉到 393 高的橫式畫布上會超出去。就讓它超出去、捲得到
   * ——那是 D38 已經決定過的行為（桌面可以比螢幕大），不是這裡的新問題。
   */
  /**
   * 這一段的寫入不進存檔。
   *
   * **轉向是看的方式，不是一次編輯。** 存下去的話，在橫式離開、回直式打開，看
   * 到的會是一份被轉過的版面——而那不是任何人排的。存檔裡永遠是使用者當初排的
   * 那一份，轉向只在畫面上發生。
   *
   * 借的是還原時用的同一個閘（`savingSuppressed`），因為那本來就是同一件事：
   * 「這些寫入是系統自己造出來的，不是有人做了什麼」。
   */
  private async withoutSaving(work: () => Promise<void>): Promise<void> {
    const was = this.savingSuppressed
    this.savingSuppressed = true
    try {
      await work()
    } finally {
      this.savingSuppressed = was
    }
  }

  /**
   * 搬一扇**釘住**的視窗。解鎖 → 搬 → 鎖回。
   *
   * ## 為什麼釘住的也要跟著轉
   *
   * **轉向不是一次搬動。** 釘住擋的是有人去搬它；轉向的時候視窗相對於桌面沒有
   * 動，是座標軸換了。把它擋下來反而會讓那扇窗相對於其他每一個東西移位——那正
   * 好違背「別動」想保住的東西。
   *
   * ## 為什麼在 shell 做，不改核心
   *
   * 核心對釘住的視窗一律拒絕 `$window/rect`，不分來源（D20），而那條規則是對
   * 的：只在 shell 藏起拖曳把手的話，agent 照樣搬得走。要讓核心認得「這一筆是
   * 轉向不是搬動」，就得給它一個新的概念，而第三條原則說核心不成長。
   *
   * 解鎖再鎖回**不是繞過那條規則**，它就是規則本身指的那條路——`$window/locked`
   * 的說明白紙黑字寫著「想移動請先解鎖」。它是三筆普通的寫入，每一筆都經過核
   * 心、都進 `$sys/activity`，人與 agent 都看得到。沒有任何東西被藏起來。
   *
   * `finally` 保證鎖得回去：中間丟了例外而讓一片牆解鎖著，比它沒跟著轉糟得多。
   */
  private async moveUnlocked(uid: string, move: () => Promise<unknown>): Promise<unknown> {
    const path = `app/${uid}/$window/locked`
    await this.bus.write(path, false, { origin: 'shell' })
    try {
      return await move()
    } finally {
      await this.bus.write(path, true, { origin: 'shell' })
    }
  }

  private async reflowAfterRotation(
    prev: { w: number; h: number },
    next: { w: number; h: number },
  ): Promise<void> {
    const template = this.activeTemplate()
    const pending: Promise<unknown>[] = []
    for (const uid of this.windows.keys()) {
      const rect = this.bus.read(`app/${uid}/$window/rect`)?.value as WindowRect | undefined
      if (!rect) continue
      const isLocked = this.bus.read(`app/${uid}/$window/locked`)?.value === true

      const move = template
        ? () => this.bus.call('$sys/cmd/snap', { uid, cell: nearestCellIndex(template.cells, prev, rect) }, { origin: 'shell' })
        :
          () => this.bus.write(`app/${uid}/$window/rect`, transposeRect(rect), { origin: 'shell' })

      pending.push(isLocked ? this.moveUnlocked(uid, move) : move())
    }
    await Promise.all(pending)
  }

  /**
   * 套用螢幕尺寸。null＝跟著瀏覽器視窗（預設）。
   *
   * 尺寸是 `"390x844"` 這種字串而不是 `{w,h}`，因為設定面板一行 JavaScript 都
   * 沒有：`data-qd-model` 綁得住一個輸入框的值，綁不出一個物件。字串讓「自己
   * 打一個尺寸」變成一個輸入框加一顆按鈕，而不是一支 app 要換一個層級。
   * 人打得出來，代理人也打得出來。
   *
   * 桌面沒有對應的尺寸設定，而且不需要有：它就是一個螢幕大，視窗拖出邊界時
   * overflow 自己會把捲動範圍撐開。「排到畫面外的視窗找得回來」是那件事的結果，
   * 不是另一個要設定的數字。
   */
  private applyScreen(spec: unknown): void {
    const size = parseSize(spec)
    if (size) {
      this.screen.style.width = `${size.w}px`
      this.screen.style.height = `${size.h}px`
      this.screen.dataset['sized'] = '1'
    } else {
      this.screen.style.width = ''
      this.screen.style.height = ''
      delete this.screen.dataset['sized']
    }
    this.reportViewport()
  }

  /** 這次啟動是從存檔還原的，還是全新開始。宿主用它決定要不要套用預設外觀。 */
  restoredFromSession = false

  static async start(options: ShellOptions): Promise<Shell> {
    const shell = new Shell(options)

    const snapshot = (await options.session?.load()) ?? null
    shell.restoredFromSession = snapshot !== null
    if (snapshot) {
      // 存檔存在就照存檔走，**即使裡面一個視窗都沒有**——
      // 「使用者把視窗全關掉」和「從來沒存過檔」是兩件不同的事。
      shell.restore(snapshot)
    } else {
      for (const spec of options.defaults ?? []) shell.launch(spec)
    }

    // 系統 app 每次都要在，否則關掉 launcher 就再也開不了東西。
    //
    // 比對的是**網址**而不是 uid：存檔裡那一份可能是還原回來的、帶著隨機 uid
    // （它以前是 defaults 的一員）。只看 uid 的話會再開一個，桌面上就出現兩個
    // 一模一樣的視窗。網址才是「這是哪一個 app」的身分（D5）。
    //
    // 但只擋「再開一個」是不夠的：目錄裡的系統 app 開得起來，所以桌面上**可能
    // 早就有兩份**，而那兩份會一起存進快照，之後每次開機都回來。兩份沒有畫面的
    // 連線 app 尤其糟——它們拿著同一把憑證,在伺服器眼裡是同一台裝置,於是互相
    // 踢掉對方。所以這裡先收斂,再補齊。
    for (const spec of options.systemApps ?? []) {
      const same = [...shell.windows.values()].filter((view) => view.url === spec.url)
      if (same.length < 2) continue
      // 留固定 uid 的那一個（agent 定址靠它），沒有的話留第一個。
      const keep = same.find((view) => view.uid === spec.uid) ?? same[0]
      for (const view of same) {
        if (view === keep) continue
        // 說出來。安靜地關掉一個使用者看得到的視窗，比留著它更難查。
        console.warn(`[QDesktop] 收斂重複的系統 app：關閉 ${view.uid}（保留 ${keep?.uid}）`)
        void shell.bus.call('$sys/cmd/close', { uid: view.uid }, { origin: 'shell' })
      }
    }

    const open = new Set([...shell.windows.values()].map((view) => view.url))
    for (const spec of options.systemApps ?? []) {
      if (open.has(spec.url)) continue
      shell.launch(spec)
      open.add(spec.url)
    }

    shell.wireAutosave()
    return shell
  }

  // ─────────────────────────────── 啟動

  launch(spec: LaunchSpec): string {
    const slug = spec.slug ?? 'app'
    const uid = spec.uid ?? `${slug}_${Math.random().toString(36).slice(2, 8)}`
    const rect: WindowRect = { x: 40, y: 40, w: 420, h: 300, ...(spec.rect ?? {}) }
    const z = spec.z ?? ++this.topZ
    if (z > this.topZ) this.topZ = z

    this.bus.declareLoading({
      uid,
      title: spec.title,
      url: spec.url,
      ...(spec.summary ? { summary: spec.summary } : {}),
      initial_window: {
        rect,
        z,
        ...(spec.state ? { state: spec.state } : {}),
        ...(spec.transparent === undefined ? {} : { transparent: spec.transparent }),
        ...(spec.fit === undefined ? {} : { fit: spec.fit }),
      },
      ...(spec.settings ? { settings: spec.settings } : {}),
    })

    this.windows.set(uid, this.createWindow(uid, spec, rect))
    this.renderDock() // 第一個視窗開起來時，dock 的 📌 才會出現
    return uid
  }

  /**
   * 開一個目錄項目。`placement` 是呼叫方指定的擺放方式，逐欄蓋過目錄的預設——
   * 只給 rect 就只換位置，其餘照目錄。
   */
  private launchFromCatalog(entry: CatalogEntry, placement?: InitialWindow): string {
    const fallback = entry.default_window
    // rect 也逐欄合併，不是整包取代：「放在這裡」通常只想指定位置，尺寸仍該
    // 用這個 app 自己合理的大小。整包換掉的話，只給 x/y 會把寬高一起洗成 0。
    const rect =
      placement?.rect || fallback?.rect
        ? { ...fallback?.rect, ...placement?.rect }
        : undefined
    const state = placement?.state ?? fallback?.state ?? (entry.headless ? 'hidden' : 'normal')
    const z = placement?.z ?? fallback?.z
    const transparent = placement?.transparent ?? fallback?.transparent
    return this.launch({
      url: entry.url,
      title: entry.title,
      summary: entry.summary,
      slug: entry.app_id,
      ...(rect ? { rect } : {}),
      state,
      ...(z === undefined ? {} : { z }),
      ...(transparent === undefined ? {} : { transparent }),
    })
  }

  private createWindow(uid: string, spec: LaunchSpec, rect: WindowRect): WindowView {
    const frame = document.createElement('div')
    frame.className = 'qd-win'
    frame.style.cssText = `left:${rect.x}px;top:${rect.y}px;width:${rect.w}px;height:${rect.h}px`

    const title = document.createElement('div')
    title.className = 'qd-title'
    const titleText = document.createElement('b')
    const statusText = document.createElement('span')
    statusText.className = 'qd-life'
    statusText.textContent = 'loading'

    const info = button('qd-btn', 'ⓘ', this.t, 'howToUse')
    const cog = button('qd-btn', '⚙', this.t, 'settings')
    const sheet = document.createElement('div')
    sheet.className = 'qd-info'
    const cogSheet = document.createElement('div')
    cogSheet.className = 'qd-info qd-set'
    const front = button('qd-btn', '⤒', this.t, 'toFront')
    const back = button('qd-btn', '⤓', this.t, 'toBack')
    const minimise = button('qd-btn', '–', this.t, 'minimise')
    const maximise = button('qd-btn', '▢', this.t, 'maximise')
    const close = button('qd-btn qd-close', '×', this.t, 'close')
    // 這顆只負責**釘**，不負責解。釘下去標題列整條收起來，它會跟著自己消失，
    // 所以它天生就只可能在「還沒釘住」的狀態下被按到——沒有第二個狀態要顯示。
    // 解除在底部 dock 的 📌（見 renderDock），那是釘住之後唯一還碰得到的表面。
    const pin = button('qd-btn', '🔒', this.t, 'lockPosition')
    title.append(titleText, statusText, info, cog, pin, front, back, minimise, maximise, close)
    // 面板掛在視窗上而不是標題列上，才不會被標題列的高度裁掉
    frame.append(sheet, cogSheet)

    const body = document.createElement('div')
    body.className = 'qd-body'
    const iframe = document.createElement('iframe')
    iframe.src = this.frameUrl(spec.url)
    const allow = this.permissionsFor(spec.url)
    if (allow) iframe.allow = allow

    const tombstone = document.createElement('div')
    tombstone.className = 'qd-tomb'
    const tombText = document.createElement('span')
    const reload = document.createElement('button')
    reload.textContent = this.t('reload')
    tombstone.append(tombText, reload)

    // agent 動這個視窗時，顯示是誰動的
    const actor = document.createElement('span')
    actor.className = 'qd-actor'

    body.append(iframe, tombstone)
    frame.append(title, body, actor)
    this.layer.append(frame)

    titleText.textContent = spec.title

    const binding = IframeBinding.bind({
      bus: this.bus,
      iframe,
      uid,
      appOrigin: new URL(spec.url, location.href).origin,
      onFault: (error) => console.warn(`[QDesktop] ${uid} fault`, error),
      /*
        元件說「有人碰我」。

        這是唯一能做到「點畫面任何地方 dock 都會回來」的路：元件住在跨來源
        iframe 裡，指標事件出不去，而宿主收得到的旁證只有自己失焦——那只在焦點
        **換過去**的那一次發生，在同一支元件裡連點十下，宿主一次都不知道。

        只叫 bumpDock，不動焦點框也不提視窗：這一聲說的是「這裡有人」，不是
        「換這一個了」。把它拿來重排疊放順序，就會變成碰一下牆整面重排。
      */
      onTouch: () => this.bumpDock(),
      onGesture: (phase, points) => this.onGesture(uid, phase, points),
    })

    // 每一顆按鈕都只是寫 topic——跟 agent 做同一件事走同一條路徑
    close.addEventListener('click', () => {
      void this.bus.call('$sys/cmd/close', { uid }, { origin: 'shell' })
    })
    const openSheet = (target: HTMLDivElement, fill: () => void) => (event: Event) => {
      event.stopPropagation()
      const open = !target.classList.contains('qd-open')
      // 一次只開一份，否則畫面上會疊好幾張
      for (const other of this.root.querySelectorAll('.qd-info.qd-open')) {
        other.classList.remove('qd-open')
      }
      if (open) {
        fill()
        target.classList.add('qd-open')
      }
    }

    info.addEventListener('click', openSheet(sheet, () => this.fillInfo(sheet, uid)))
    cog.addEventListener('click', openSheet(cogSheet, () => this.fillSettings(cogSheet, uid)))
    pin.addEventListener('click', () => {
      void this.bus.write(`app/${uid}/$window/locked`, true, { origin: 'shell' })
    })
    front.addEventListener('click', () => this.bringToFront(uid))
    back.addEventListener('click', () => this.sendToBack(uid))
    minimise.addEventListener('click', () => {
      void this.bus.write(`app/${uid}/$window/state`, 'min', { origin: 'shell' })
    })
    maximise.addEventListener('click', () => {
      const current = this.bus.read(`app/${uid}/$window/state`)?.value
      void this.bus.write(`app/${uid}/$window/state`, current === 'max' ? 'normal' : 'max', {
        origin: 'shell',
      })
    })
    reload.addEventListener('click', () => {
      // 重載後 app 重新註冊，從 topic 灌回狀態——不需要 app 自己存檔
      iframe.src = this.frameUrl(spec.url)
    })
    frame.addEventListener(
      'pointerdown',
      (event) => {
        // 標題列上的按鈕不觸發「提到最上層」。
        //
        // 那些鈕每一顆都已經把自己要的事說完了——鎖定、送到最底、最小化——而
        // 這裡再替它們補一個 z 的變更，等於在它們說完之後加一句它們沒說的話。
        // 症狀最清楚的是：把視窗送到最底，再按鎖定，它又跳回最上面。
        //
        // 焦點環仍然給：按一個視窗的按鈕，確實是在跟那個視窗互動。動的是深淺，
        // 不是「哪一個是現在這一個」。
        if (!(event.target as Element | null)?.closest?.('.qd-btn') && this.mayAutoRaise(uid)) {
          this.bringToFront(uid)
        }
        this.setFocusRing(uid)
      },
      true,
    )
    this.makeDraggable(uid, title, frame)
    this.makeResizable(uid, frame)

    return {
      uid,
      url: spec.url,
      frame,
      titleText,
      statusText,
      iframe,
      tombstone,
      binding,
      maximise,
      actor,
      glowTimer: null,
    }
  }

  // ─────────────────────────────── 誰在動、人在看哪

  /**
   * 焦點框**只是 shell 的本地狀態，不進 topic 樹**。
   *
   * 曾經考慮做成 $sys/focus 給 agent 讀（「人正在用這個，先別動」），
   * 後來拿掉了，理由有兩個：
   *   1. 偵測本身不可靠——點進已經有焦點的 iframe 不會產生任何父層事件，
   *      捲動與閱讀更是完全偵測不到。讓 agent 對著一個會說謊的訊號寫決策，
   *      比沒有訊號更糟。
   *   2. 在「Agent 為主，人監看」的模型裡，人是觀察者不是共同操作者，
   *      鎖定之後更是如此。
   */
  private setFocusRing(uid: string | null): void {
    for (const view of this.windows.values()) {
      view.frame.classList.toggle('qd-focus', view.uid === uid)
    }
  }

  private wireFocusTracking(): void {
    // 點進 iframe 時父頁面收不到事件，只能靠 blur 之後看 activeElement
    addEventListener('blur', () => {
      const active = document.activeElement
      if (!(active instanceof HTMLIFrameElement)) return
      /*
        點在元件上也要把 dock 叫回來。

        `root` 上的 pointerdown 已經在做這件事，但它**只涵蓋 shell 自己畫的東
        西**：元件住在跨來源 iframe 裡，裡面的事件不會冒泡出來。而元件佔掉的正
        是螢幕的絕大部分，所以「點了畫面卻叫不出 dock」是常態而不是例外——那條
        列淡出之後就找不到了。

        放在 setFocusRing 之前，因為這是同一件事的兩半：這一刻我們才知道人碰了
        那扇窗，焦點框與 dock 都該跟著醒過來。
      */
      this.bumpDock()
      for (const view of this.windows.values()) {
        if (view.iframe === active) {
          this.setFocusRing(view.uid)
          // 也要提到最上層。框上的 pointerdown 已經在做這件事，但那只涵蓋
          // shell 自己畫的部分——點在 app 的內容上時事件被跨來源 iframe 吃掉，
          // 於是視窗留在下面，它的標題列繼續被別人蓋著，等於再也抓不到。
          if (this.mayAutoRaise(view.uid)) this.bringToFront(view.uid)
          return
        }
      }
    })

    // 從元件回到桌面（點空白處、或切回這個分頁）也算互動。blur 那條管的是
    // 「進到 iframe」，這條管的是「回來」——兩個方向都要叫得回 dock。
    addEventListener('focus', () => this.bumpDock())

    this.root.addEventListener('pointerdown', (event) => {
      // 任何觸控都算「有互動」——喚出淡出中的 dock，並重新開始閒置計時。
      this.bumpDock()
      if (event.target === this.root) this.setFocusRing(null)
      // 點到說明以外的地方就收起來——它蓋著整個視窗，留著會擋住 app 本身
      const target = event.target as Element | null
      // 標題列與縮放把手不算「點到別的地方」：搬動或縮放一個視窗，不該把正在
      // 讀的說明收起來——那兩件事本來就常常要一起做（面板太小就是要拉大）。
      if (!target?.closest?.('.qd-info, .qd-btn, .qd-title, .qd-rz')) {
        for (const open of this.root.querySelectorAll('.qd-info.qd-open')) {
          open.classList.remove('qd-open')
        }
      }
      if (this.menu && !target?.closest?.('.qd-menu')) this.dismissMenu()
    })

    // 桌布上的右鍵。位置寫進 $sys/menu/at，選單本身由下面那個訂閱畫出來。
    //
    // 為什麼經過一個 topic 而不是直接畫：這樣**寫那個節點就等於按右鍵**，
    // agent 叫得出同一個選單，時間軸上也看得到「選單開了」。人做得到的事，
    // AI 必然做得到——連叫出選單這件事也不例外。
    //
    // 這與 D24 拿掉的 $sys/focus 不同，值得說清楚：焦點是用猜的，猜不準；
    // 右鍵是 shell 自己元素上的一個真實事件，target 就是桌布本身，
    // 不會誤判。可靠的訊號才配進 topic 樹。
    this.root.addEventListener('contextmenu', (event) => {
      // 空白處按的，才是桌布上的右鍵；視窗上的歸該 app 自己。視窗那一層鋪滿
      // 整個桌面，所以「桌布」現在有兩種 target：root 本身，和空著的那一層。
      if (event.target !== this.root && event.target !== this.layer) return
      event.preventDefault()
      // 對著**畫布**算:視窗的座標是畫布座標,而畫布可能被捲動過。用 root 的話,
      // 捲到右邊之後開的選單會落在錯的地方。
      const box = this.canvas.getBoundingClientRect()
      void this.bus.write(
        '$sys/menu/at',
        { x: Math.round(event.clientX - box.left), y: Math.round(event.clientY - box.top) },
        { origin: 'shell' },
      )
    })

    this.bus.subscribe('$sys/menu/at', (m) => {
      if (m.type !== 'state') return
      const at = m.value as { x: number; y: number } | null
      if (at) this.openMenu(at)
      else this.closeMenu()
    })

    // 活動紀錄是現成的：誰、動了什麼、成功還是被擋
    this.bus.subscribe('$sys/activity', (m) => {
      if (m.type !== 'event') return
      const activity = m.payload as {
        path: string
        origin: string | null
        gesture: boolean
        ok: boolean
      }
      // shell 就是人直接操作視窗框，你自己做的事不需要再提示
      if (!activity.origin || activity.origin === 'shell') return
      // 因果連線：光暈只說「這裡被動了」，連線說「是那邊造成的」
      this.drawArc(activity.origin, uidOf(activity.path), activity.ok)
      const view = this.viewFor(activity.path)
      if (!view) return
      this.flashActivity(view, activity)
    })
  }

  // ─────────────────────────────── 因果連線

  /**
   * 從發起者的視窗畫一道弧線到被影響的視窗。
   *
   * 資料全部是現成的——`$sys/activity` 有誰跟動了哪裡，兩邊的幾何都在
   * `$window/rect` 上。看不見的視窗（最小化、無頭 app）不畫，因為
   * 從一個看不到的地方長出線只會讓人更困惑。
   */
  private drawArc(fromUid: string, toUid: string, ok: boolean): void {
    const svg = this.arcs
    if (!svg || !toUid || fromUid === toUid) return

    const from = this.centreOf(fromUid)
    const to = this.centreOf(toUid)
    if (!from || !to) return

    // 同一瞬間太多條線只會變成雜訊
    while (svg.childElementCount >= MAX_ARCS) svg.firstElementChild?.remove()

    const id = `qd-arc-${this.nextArcId++}`
    const group = document.createElementNS(SVG_NS, 'g')

    const path = document.createElementNS(SVG_NS, 'path')
    path.setAttribute('id', id)
    path.setAttribute('class', `qd-arc ${ok ? 'qd-arc-ok' : 'qd-arc-no'}`)
    path.setAttribute('d', arcPath(from, to))

    // 沿著線跑的小圓點——方向感靠它，不然看不出誰影響誰
    const dot = document.createElementNS(SVG_NS, 'circle')
    dot.setAttribute('r', '4')
    dot.setAttribute('class', ok ? 'qd-dot-ok' : 'qd-dot-no')
    const motion = document.createElementNS(SVG_NS, 'animateMotion')
    motion.setAttribute('dur', '0.42s')
    motion.setAttribute('fill', 'freeze')
    const mpath = document.createElementNS(SVG_NS, 'mpath')
    mpath.setAttribute('href', `#${id}`)
    motion.append(mpath)
    dot.append(motion)

    group.append(path, dot)
    svg.append(group)

    const length = path.getTotalLength()
    path.style.strokeDasharray = String(length)
    path.animate([{ strokeDashoffset: length }, { strokeDashoffset: 0 }], {
      duration: 420,
      easing: 'cubic-bezier(.2,.8,.3,1)',
      fill: 'forwards',
    })
    const fade = group.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: 380,
      delay: ok ? 520 : 1_100,
      fill: 'forwards',
    })
    fade.addEventListener('finish', () => group.remove())
  }

  /** 視窗中心點。看不見的回傳 null。 */
  private centreOf(uid: string): { x: number; y: number } | null {
    if (!this.windows.has(uid)) return null
    const state = this.bus.read(`app/${uid}/$window/state`)?.value
    if (state === 'hidden' || state === 'min') return null
    if (state === 'max') {
      return { x: this.canvas.clientWidth / 2, y: this.canvas.clientHeight / 2 }
    }
    const rect = this.bus.read(`app/${uid}/$window/rect`)?.value as WindowRect | undefined
    if (!rect) return null
    return { x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 }
  }

  /**
   * 三種強度，對應三種確定程度：
   *   - 失敗（紅） —— 不管誰做的，被擋下來都要看得見
   *   - 有使用者手勢（淡） —— 人透過某個介面做的，只是提示因果
   *   - 沒有手勢（亮） —— app 自主行為，這才是「agent 在動」
   */
  private flashActivity(
    view: WindowView,
    activity: { origin: string | null; gesture: boolean; ok: boolean },
  ): void {
    const origin = activity.origin ?? '?'
    const denied = !activity.ok
    const relayed = activity.gesture

    view.actor.textContent = denied ? `${origin} ✕` : relayed ? `↳ ${origin}` : origin
    view.frame.classList.toggle('qd-agent', !denied && !relayed)
    view.frame.classList.toggle('qd-relay', !denied && relayed)
    view.frame.classList.toggle('qd-agent-denied', denied)

    if (view.glowTimer !== null) clearTimeout(view.glowTimer)
    view.glowTimer = setTimeout(
      () => {
        view.glowTimer = null
        view.frame.classList.remove('qd-agent', 'qd-relay', 'qd-agent-denied')
      },
      denied ? 1_800 : relayed ? 500 : 900,
    )
  }

  /** 釘住或最大化時，位置與尺寸不該再被拖動 */
  private isGeometryFrozen(uid: string): boolean {
    return (
      this.bus.read(`app/${uid}/$window/locked`)?.value === true ||
      this.bus.read(`app/${uid}/$window/state`)?.value === 'max'
    )
  }

  /** 拖曳或縮放放開時吸附到最近的格子。free 就什麼都不做。 */
  private snapIfTemplated(uid: string): void {
    this.hideGuides()
    if (!this.activeTemplate()) return
    // 走 $sys/cmd/snap 而不是自己算——人放開滑鼠與 agent 呼叫走同一條路徑
    void this.bus.call('$sys/cmd/snap', { uid }, { origin: 'shell' })
  }

  // ─────────────────────────────── 版面輔助線

  private activeTemplate(): LayoutTemplate | null {
    const id = this.bus.read('$sys/layout/template')?.value
    if (id === 'free' || typeof id !== 'string') return null
    const templates = (this.bus.read('$sys/layout/templates')?.value ?? []) as LayoutTemplate[]
    return templates.find((candidate) => candidate.id === id) ?? null
  }

  /**
   * 畫出目前板型的格子。
   *
   * 格子本來就是 0–1 的比例，直接用百分比定位，所以桌面縮放時不用重畫；
   * 只有 gap 是像素，交給內層元素的 inset 處理。
   */
  private renderGuides(): void {
    const guides = this.guides
    if (!guides) return
    guides.replaceChildren()
    this.slots.length = 0

    const template = this.activeTemplate()
    if (!template) return

    const inset = (template.gap ?? 0) / 2
    for (const cell of template.cells) {
      const slot = document.createElement('div')
      slot.className = 'qd-slot'
      slot.style.cssText = `left:${cell.x * 100}%;top:${cell.y * 100}%;width:${cell.w * 100}%;height:${cell.h * 100}%`
      const inner = document.createElement('i')
      inner.style.inset = `${inset}px`
      slot.append(inner)
      guides.append(slot)
      this.slots.push(slot)
    }
  }

  private showGuides(): void {
    if (!this.activeTemplate()) return
    this.guides?.classList.add('qd-on')
    if (this.guideTimer !== null) {
      clearTimeout(this.guideTimer)
      this.guideTimer = null
    }
  }

  private hideGuides(): void {
    this.guides?.classList.remove('qd-on')
    for (const slot of this.slots) slot.classList.remove('qd-target')
  }

  /** 拖曳過程中即時標出會吸到哪一格 */
  private markTarget(rect: WindowRect): void {
    const template = this.activeTemplate()
    if (!template) return
    const viewport = { w: this.canvas.clientWidth, h: this.canvas.clientHeight }
    // 用核心匯出的同一份判斷，看到的框才會等於實際吸到的格
    const index = nearestCellIndex(template.cells, viewport, rect)
    for (let i = 0; i < this.slots.length; i++) {
      ;(this.slots[i] as HTMLElement).classList.toggle('qd-target', i === index)
    }
  }

  /** 剛選好板型時閃一下，讓人看清楚版面長什麼樣 */
  private flashGuides(): void {
    this.showGuides()
    this.guideTimer = setTimeout(() => {
      this.guideTimer = null
      this.hideGuides()
    }, 1_400)
  }

  // ─────────────────────────────── 宿主能力 → 無頭 app

  /**
   * 把 shell 才有的能力（清存檔、立即存檔）暴露成一個無頭 app。
   *
   * 這跟未來原生殼提供相機、藍牙的做法是同一套：
   * **宿主能力一律以無頭 QApp 的形式進入 topic 樹**，核心不用知道有這回事。
   */
  private mountSessionApp(): void {
    if (!this.store) return

    this.bus.declareLoading({
      uid: SESSION_APP_UID,
      title: '桌面工作階段',
      summary: '桌面本身的存檔控制',
      initial_window: { state: 'hidden' },
    })

    const app = this.bus.register({
      uid: SESSION_APP_UID,
      title: '桌面工作階段',
      summary: '桌面本身的存檔控制。可立即存檔或清除存檔回到預設桌面。',
      groups: { data: '存檔狀態', cmd: '存檔操作' },
      nodes: [
        {
          path: 'data/saved_at',
          kind: 'state',
          access: 'r',
          schema: { type: 'integer', nullable: true },
          description: '上一次存檔的時間戳（epoch 毫秒）。尚未存過為 null。',
        },
        {
          path: 'data/account',
          kind: 'state',
          access: 'r',
          schema: { type: 'object', nullable: true },
          description:
            '把這台桌面解鎖的 QSyn 身分：{user, email, workspace, workspace_id, paired}。沒有登入閘時是 null。**paired 為 false 表示已登入但沒配到裝置**，代理人因此接不到這台桌面。workspace 是配對當下作用中的那一個——代理人若在別的工作空間，就查不到這台桌面。這裡沒有任何憑證，只有名字。',
        },
        {
          path: 'data/save_error',
          kind: 'state',
          access: 'r',
          schema: { type: 'string', nullable: true },
          description:
            '上一次存檔失敗的理由，成功時是 null。**同步失敗不等於資料沒了**——本機那一份永遠先寫，所以這裡說的是「沒有同步到 QSyn」，桌面本身還在。',
        },
        {
          path: 'cmd/save',
          kind: 'action',
          access: 'w',
          schema: { type: 'null' },
          description: '立即把目前的視窗配置存起來，不等防抖。回傳 {saved_at}。',
        },
        {
          path: 'cmd/reset',
          kind: 'action',
          access: 'w',
          risk: 'high',
          schema: { type: 'null' },
          description:
            '清除存檔並重新載入桌面，回到預設配置。**目前開啟的視窗會全部關閉**，未存檔的內容可能遺失。此操作不可逆。',
        },
      ],
    })

    app.publish('data/saved_at', null)
    app.publish('data/save_error', null)
    app.publish('data/account', this.account)
    app.onCall((relPath) => {
      if (relPath === 'cmd/save') {
        // 等它真的落地才回答。按下按鈕之後立刻說「好了」，正是這顆按鈕要修的
        // 那個毛病。
        return this.flushSave().then(() => ({
          saved_at: this.bus.read(`app/${SESSION_APP_UID}/data/saved_at`)?.value ?? null,
        }))
      }
      if (relPath === 'cmd/reset') {
        void this.resetSession()
        return { ok: true }
      }
      throw new Error(`未知的 action ${relPath}`)
    })

    this.sessionApp = app
  }

  // ─────────────────────────────── Session

  private restore(snapshot: SessionSnapshot): void {
    this.restoring = true
    try {
      // 語言先還原，再開視窗。反過來的話每個 app 會先用預設語言畫一次、再被
      // 改回來，畫面上就是一閃。
      if (typeof snapshot.locale === 'string') {
        void this.bus.write('$sys/locale', snapshot.locale, { origin: 'shell' })
      }
      // 螢幕跟桌布同一個層級:它是這個桌面的外觀,所以跟著桌面走。
      if (snapshot.screen !== undefined) {
        void this.bus.write('$sys/screen', snapshot.screen ?? null, { origin: 'shell' })
      }
      if (typeof snapshot.wallpaper.color === 'string') {
        void this.bus.write('$sys/wallpaper/color', snapshot.wallpaper.color, { origin: 'shell' })
      }
      if (snapshot.wallpaper.image !== undefined) {
        void this.bus.write('$sys/wallpaper/image', snapshot.wallpaper.image, { origin: 'shell' })
      }
      if (typeof snapshot.layout === 'string') {
        void this.bus.write('$sys/layout/template', snapshot.layout, { origin: 'shell' })
      }
      // 存檔裡的 app 可能已經不在目錄裡了——上架下架、換網址、demo 清掉東西。
      // 照著存檔開會得到一個指向 404 的空框，registry 裡還會多一筆永遠 loading
      // 的實例；那個框對人沒有意義，對 agent 更糟（它會以為那是個能用的工具）。
      //
      // 但也不能靜靜丟掉：使用者的視窗不見了是他該知道的事。所以丟掉的走活動
      // 紀錄，時間軸上看得到。
      const { keep: known, dropped } = restorable(snapshot.instances, this.catalog)
      // 上限也套在還原上。只擋新開的話，一個從高方案降下來的桌面會永遠停在超額
      // 狀態——那個上限對它等於不存在。
      const { keep, overflow } = withinLimit(known, this.readLimit(), this.systemUrls)

      // instances 已依 z 排序，照順序開就會得到相同的疊放
      for (const instance of keep) {
        this.launch({
          url: instance.url,
          title: instance.title,
          uid: instance.uid,
          rect: instance.rect,
          state: instance.state,
          z: instance.z,
          transparent: instance.transparent,
          // 存檔裡的座標是使用者（或代理人）決定過的，不是建議。夾住它等於每次
          // 重整都把排到畫面外的視窗拉回來——而畫布會跟著視窗長，正是為了讓那
          // 件事成立。
          fit: false,
          ...(instance.settings ? { settings: instance.settings } : {}),
        })
        // 釘住要在視窗建好之後才寫，否則核心會擋掉還原它位置的那一次寫入
        if (instance.locked) {
          void this.bus.write(`app/${instance.uid}/$window/locked`, true, { origin: 'shell' })
        }
      }

      if (dropped.length > 0) {
        // 帶上網址，不只是標題：會落到這裡的原因通常是「網址換了」而不是
        // 「app 沒了」，而只印標題的話那兩件事看起來一模一樣。
        console.warn(
          '[QDesktop] 這些視窗已不在目錄裡，沒有還原：\n' +
            dropped.map((i) => `  ${i.title || i.uid}  ${i.url}`).join('\n'),
        )
      }
      if (overflow.length > 0) {
        // 說出來，不要無聲少幾個。而且要說清楚原因是方案上限而不是壞掉——
        // 那兩件事在畫面上長得一模一樣。清單當第二個參數傳，devtools 才展得開。
        console.warn(
          `[QDesktop] 這個方案最多放 ${this.readLimit()} 個元件，以下沒有還原：`,
          overflow.map((i) => `${i.title || i.uid}  ${i.url}`),
        )
      }
    } finally {
      this.restoring = false
    }
  }

  private wireAutosave(): void {
    if (!this.store) return
    const touch = (path: string): void => {
      this.saveReason = path
      this.scheduleSave()
    }
    this.bus.subscribe('$sys/registry/instances', (m) => touch(m.path))
    this.bus.subscribe('$sys/wallpaper/+', (m) => touch(m.path))
    this.bus.subscribe('app/+/$window/+', (m) => touch(m.path))
    // app 自己的設定也要存。只認 rw 的 state：`r` 是 app 算出來的，一個每秒
    // 更新十次的量測值會讓 debounce 永遠不到期，反而變成從來不存檔。
    this.bus.subscribe('app/+/#', (message) => {
      if (message.type !== 'state') return
      const def = this.bus.describe(message.path)
      if (def?.kind === 'state' && def.access === 'rw') touch(message.path)
    })
    // 分頁關閉前把最後一次拖曳寫進去。這裡沒有辦法等結果——頁面正在消失，
    // 而且瀏覽器不會為了一個 await 多留一刻。
    addEventListener('pagehide', () => void this.flushSave().catch(() => {}))

    // 一開機就先說「已儲存」——這一刻畫面上的東西確實就是存檔裡的東西。
    this.setSaveState('saved')
  }

  private scheduleSave(): void {
    if (!this.store || this.restoring) return
    // 上限：一直有東西在動的時候，debounce 每次都被推遲，結果是從來不存檔。
    // 拖曳一個視窗只要幾百毫秒所以看不出來，但一個持續寫入的 rw 節點會讓它
    // 永遠不到期——那正是這次要存的東西。
    const now = Date.now()
    this.saveDeadline ??= now + MAX_SAVE_WAIT_MS
    if (this.saveTimer !== null) clearTimeout(this.saveTimer)
    const wait = Math.max(0, Math.min(SAVE_DEBOUNCE_MS, this.saveDeadline - now))
    // catch 掉:失敗的理由已經進了 data/save_error(而且畫面上看得到)。這裡再
    // 讓它變成一個未處理的 rejection，只會在主控台洗掉真正的錯誤。
    this.saveTimer = setTimeout(() => void this.flushSave().catch(() => {}), wait)
  }

  /**
   * 存檔，並且**照實回報**。
   *
   * 以前這裡是 `void store.save(...)` 之後緊接著發布時間戳——存檔成不成功都一
   * 樣。畫面上寫著「剛剛儲存」而東西其實沒存進去，是比沒有那行字更糟的事：它
   * 讓人放心地關掉分頁。
   *
   * 所以等它回來才動 saved_at，失敗就把理由放進 save_error。同步失敗**不等於
   * 資料沒了**——本機那份先寫，所以訊息說的是「沒有同步」，不是「沒有存」。
   */
  private async flushSave(): Promise<void> {
    if (!this.store || this.savingSuppressed) return
    if (this.saveTimer !== null) {
      clearTimeout(this.saveTimer)
      this.saveTimer = null
    }
    this.saveDeadline = null

    // 一次只飛一個。撞上正在進行的那一次就記下來,等它落地再存——不是排隊送出
    // 第二個,因為第二個手上的 rev 已經過期了。
    if (this.saving) {
      this.saveAgain = true
      return this.saving
    }

    const run = this.runSave()
    this.saving = run
    try {
      await run
    } finally {
      this.saving = null
      if (this.saveAgain) {
        this.saveAgain = false
        // 用排程而不是直接遞迴:落地之後可能又累積了好幾筆變動,讓 debounce 把
        // 它們併成一次。
        this.scheduleSave()
      }
    }
  }

  private async runSave(): Promise<void> {
    const store = this.store
    if (!store) return

    const at = Date.now()
    const snapshot = this.bus.sessionSnapshot()
    // saved_at 每次都不一樣，所以比對的時候要把它拿掉——不然「沒變」永遠不成立。
    const fingerprint = JSON.stringify({ ...snapshot, saved_at: 0 })
    if (fingerprint === this.lastSaved) {
      this.setSaveState('saved')
      return
    }

    this.setSaveState('saving')
    try {
      await store.save(snapshot)
      this.lastSaved = fingerprint
      this.sessionApp?.publish('data/saved_at', at)
      this.sessionApp?.publish('data/save_error', null)
      this.setSaveState('saved')
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : String(cause)
      this.sessionApp?.publish('data/save_error', message)
      this.setSaveState('error', message)
      throw cause
    }
  }

  /** 清掉存檔並重新整理，回到預設桌面 */
  async resetSession(): Promise<void> {
    // 必須先擋掉存檔：location.reload() 會觸發 pagehide，
    // 那個 handler 會把剛清掉的存檔原封不動寫回去。
    this.savingSuppressed = true
    if (this.saveTimer !== null) clearTimeout(this.saveTimer)
    this.saveTimer = null
    await this.store?.clear()
    location.reload()
  }

  // ─────────────────────────────── 使用者動作 → 寫回 topic

  private makeDraggable(uid: string, handle: HTMLElement, frame: HTMLDivElement): void {
    let dragging = false
    let originX = 0
    let originY = 0
    let startRect: WindowRect | null = null
    let queued: { x: number; y: number } | null = null

    const flush = (): void => {
      if (!queued) return
      const next = queued
      queued = null
      // 一次寫入即原子更新——rect 支援部分寫入，不會產生中間態
      void this.bus.write(`app/${uid}/$window/rect`, next, { origin: 'shell' })
    }

    /*
      標題列上的按鈕跟拖曳共用同一片可觸控表面，用**位移量**分辨意圖：
      沒動就是點、動了就是拖。手機上按鈕擠成一排的時候，「按鈕之間沒縫」就
      不再等於「拖不了」——手指落在按鈕上、動 6px 就開始拖那扇窗，pointer
      事件位移超過閾值時瀏覽器**自動不派 click**（pointer events spec），所
      以連原本那顆按鈕的動作都不會誤觸。這一段 UX 換來的是：不必為了拖而
      特意瞄準縫隙，也不必因此裁掉按鈕。

      resize 走的還是舊路（`event.target.tagName === 'BUTTON'` 這條路它用不到，
      resize 把手上本來就沒有按鈕）——那條 wireResize 也是類似的模式，這邊不動。
    */
    const DRAG_THRESHOLD = 6
    let pending = false

    handle.addEventListener('pointerdown', (event) => {
      if (this.isGeometryFrozen(uid)) return
      const snapshot = this.bus.read(`app/${uid}/$window/rect`)
      if (!snapshot) return
      startRect = snapshot.value as WindowRect
      originX = event.clientX
      originY = event.clientY
      // 「準備狀態」——還不 capture、也還沒進 dragging 樣式：這一秒可能是一次
      // 按鈕點擊，也可能是拖曳的起手，等 pointermove 看動了多少才知道。
      pending = true
    })

    handle.addEventListener('pointermove', (event) => {
      if (pending && !dragging) {
        const dx = event.clientX - originX
        const dy = event.clientY - originY
        if (dx * dx + dy * dy < DRAG_THRESHOLD * DRAG_THRESHOLD) return
        pending = false
        dragging = true
        handle.setPointerCapture(event.pointerId)
        frame.classList.add('qd-dragging')
        this.showGuides()
      }
      if (!dragging || !startRect) return
      queued = {
        x: Math.round(startRect.x + event.clientX - originX),
        y: Math.round(startRect.y + event.clientY - originY),
      }
      this.markTarget({ ...startRect, ...queued })
      requestAnimationFrame(flush)
    })

    const stop = (event: PointerEvent): void => {
      pending = false
      if (!dragging) return
      dragging = false
      startRect = null
      flush()
      this.snapIfTemplated(uid)
      handle.releasePointerCapture(event.pointerId)
      frame.classList.remove('qd-dragging')
    }
    handle.addEventListener('pointerup', stop)
    handle.addEventListener('pointercancel', stop)
  }

  /**
   * 給**原生宿主**（Flutter InAppWebView 之類）呼叫的拖曳 API——長按之後由
   * 那邊的手勢識別器往這裡傳座標。存在的理由：全螢幕元件可以真的頂到 status
   * bar，那條線變成 iOS 系統手勢區、標題列拖不動；native 的長按事件在 WebView
   * **之外**觸發，繞開這個限制，於是「頂到底又能拖」兩件事同時成立。
   *
   * 三支 API：
   *   window.__qdesktopWindowDrag.begin(x, y)   決定要拖哪一扇（hit-test），
   *                                              記下 startRect 跟起手座標，
   *                                              加 qd-dragging class；回傳
   *                                              true 表示可以拖，false 表示
   *                                              沒抓到視窗（例如點在桌布上）
   *   window.__qdesktopWindowDrag.update(x, y)  改 rect 的 x/y，發布到 bus；
   *                                              markTarget 讓對齊 slot 顯示
   *   window.__qdesktopWindowDrag.end()         結尾：snap 到 template slot、
   *                                              移除 class、收 guides
   *
   * 座標是 CSS 像素（跟 `document.elementFromPoint` 用的相同），原生那邊拿
   * Flutter 的 `globalPosition` 傳過來，DIP → CSS 1:1（iOS 上 devicePixelRatio
   * 已被瀏覽器把 CSS px 對齊到 pt）。
   */
  private wireHostDragAPI(): void {
    const state: {
      uid: string | null
      startRect: WindowRect | null
      originX: number
      originY: number
    } = { uid: null, startRect: null, originX: 0, originY: 0 }

    const findUidAt = (x: number, y: number): string | null => {
      // elementFromPoint 尊重 z-order，最上層先拿到——正是「拖到最上面那扇」。
      // 走 .qd-win 而不是 .qd-title：全螢幕時 title 可能不在游標下（游標在
      // 內容區），還是能拖。
      const el = document.elementFromPoint(x, y) as HTMLElement | null
      const frame = el?.closest?.('.qd-win') as HTMLDivElement | undefined | null
      if (!frame) return null
      for (const [uid, view] of this.windows) {
        if (view.frame === frame) return uid
      }
      return null
    }

    const api = {
      begin: (x: number, y: number): boolean => {
        const uid = findUidAt(x, y)
        if (!uid) return false
        if (this.isGeometryFrozen(uid)) return false
        const snapshot = this.bus.read(`app/${uid}/$window/rect`)
        if (!snapshot) return false
        state.uid = uid
        state.startRect = snapshot.value as WindowRect
        state.originX = x
        state.originY = y
        const view = this.windows.get(uid)
        view?.frame.classList.add('qd-dragging')
        this.showGuides()
        return true
      },
      update: (x: number, y: number): void => {
        if (!state.uid || !state.startRect) return
        const next = {
          x: Math.round(state.startRect.x + x - state.originX),
          y: Math.round(state.startRect.y + y - state.originY),
        }
        this.markTarget({ ...state.startRect, ...next })
        void this.bus.write(`app/${state.uid}/$window/rect`, next, { origin: 'shell' })
      },
      end: (): void => {
        if (!state.uid) return
        const uid = state.uid
        const view = this.windows.get(uid)
        view?.frame.classList.remove('qd-dragging')
        this.snapIfTemplated(uid)
        this.hideGuides()
        state.uid = null
        state.startRect = null
      },
    }
    ;(window as unknown as { __qdesktopWindowDrag: typeof api }).__qdesktopWindowDrag = api
  }

  /**
   * 八個方向的縮放把手。
   *
   * 與拖曳走完全相同的路徑：算出新的 rect，寫回 `$window/rect`。
   * 畫面會變不是因為這裡改了 style，而是因為 topic 變了——
   * 所以 agent 寫 rect 跟人拉邊框，結果完全一致。
   */
  private makeResizable(uid: string, frame: HTMLDivElement): void {
    for (const dir of RESIZE_DIRECTIONS) {
      const handle = document.createElement('div')
      handle.className = `qd-rz qd-rz-${dir}`
      frame.append(handle)
      this.wireResize(uid, frame, handle, dir)
    }
  }

  private wireResize(
    uid: string,
    frame: HTMLDivElement,
    handle: HTMLElement,
    dir: string,
  ): void {
    let resizing = false
    let originX = 0
    let originY = 0
    let start: WindowRect | null = null
    let queued: WindowRect | null = null

    const flush = (): void => {
      if (!queued) return
      const next = queued
      queued = null
      void this.bus.write(`app/${uid}/$window/rect`, next, { origin: 'shell' })
    }

    handle.addEventListener('pointerdown', (event) => {
      if (this.isGeometryFrozen(uid)) return
      const snapshot = this.bus.read(`app/${uid}/$window/rect`)
      if (!snapshot) return
      resizing = true
      start = snapshot.value as WindowRect
      originX = event.clientX
      originY = event.clientY
      handle.setPointerCapture(event.pointerId)
      frame.classList.add('qd-dragging')
      this.showGuides()
      event.stopPropagation()
    })

    handle.addEventListener('pointermove', (event) => {
      if (!resizing || !start) return
      queued = resolveResize(start, dir, event.clientX - originX, event.clientY - originY)
      this.markTarget(queued)
      requestAnimationFrame(flush)
    })

    const stop = (event: PointerEvent): void => {
      if (!resizing) return
      resizing = false
      start = null
      flush()
      this.snapIfTemplated(uid)
      handle.releasePointerCapture(event.pointerId)
      frame.classList.remove('qd-dragging')
    }
    handle.addEventListener('pointerup', stop)
    handle.addEventListener('pointercancel', stop)
  }

  /**
   * 這扇窗可不可以被**自動**提到最上層。
   *
   * 釘住的不行。「⤓ 送到最底」與「碰一下就浮上來」在一片釘好的牆上是直接打架
   * 的：把一個當背景的元件壓到最底，然後手指碰它一下，它又回到最前面——那個送
   * 到最底等於按了個寂寞。
   *
   * 這不違反 D20 說的「釘住只擋位置，`state` / `z` / `transparent` 照常」：那
   * 講的是**明確的寫入**——人或代理人指名要改 z，照樣改得動（⤒ 那顆鈕就是）。
   * 這裡擋掉的是 shell 自己補上的那一句沒有人要求的 z 變更，跟標題列按鈕不觸發
   * 提升是同一個理由：不要在使用者說完之後，替他多說一句他沒說的話。
   */
  private mayAutoRaise(uid: string): boolean {
    return this.bus.read(`app/${uid}/$window/locked`)?.value !== true
  }

  /**
   * 一次兩指手勢。**一根手指是元件的，兩根是桌面的**（見 wire.md 的 `gesture`）。
   *
   * 只記按下那一刻的矩形，之後每一格都對著它算——逐格累加會把取整誤差存起來，
   * 一次長距離的拖曳結束時視窗會偏掉，而那種偏移查不出來源。
   *
   * 釘住或最大化的不受理（`isGeometryFrozen`）：核心對釘住的視窗一律拒絕 rect
   * 寫入（D20），送過去只會在時間軸上留一串 `E_DENIED`——而那不是錯誤，是釘住
   * 正在生效。**捏一個釘住的視窗不會動，那是對的。**
   *
   * 手指放開才吸附板型，跟拖曳放開走同一條路。
   */
  private onGesture(
    uid: string,
    phase: GesturePhase,
    points: readonly GesturePoint[],
  ): void {
    const view = this.windows.get(uid)
    if (phase === 'end') {
      if (this.gestures.delete(uid)) {
        view?.frame.classList.remove('qd-gesturing')
        this.snapIfTemplated(uid)
      }
      return
    }
    if (points.length === 0) return
    const now = pinchOf(points)

    if (phase === 'start') {
      if (this.isGeometryFrozen(uid)) return
      const rect = this.bus.read(`app/${uid}/$window/rect`)?.value as WindowRect | undefined
      if (!rect) return
      this.gestures.set(uid, { rect, from: now, queued: null })
      // 補間動畫在手勢期間關掉，理由同 D25：每一格都往目標 ease 180ms 的話，手
      // 指已經到了視窗還在路上。
      view?.frame.classList.add('qd-gesturing')
      this.showGuides()
      this.bumpDock()
      return
    }

    const held = this.gestures.get(uid)
    if (!held) return
    const next = pinchRect(held.rect, held.from, now)
    this.markTarget(next)
    // 一格只寫一次。touchmove 一秒來六十次以上，每一次都寫等於讓匯流排與 DOM
    // 各跑六十輪——跟拖曳走同一套節流。
    const first = held.queued === null
    held.queued = next
    if (!first) return
    requestAnimationFrame(() => {
      const pending = this.gestures.get(uid)
      if (!pending?.queued) return
      const rect = pending.queued
      pending.queued = null
      void this.bus.write(`app/${uid}/$window/rect`, rect, { origin: 'shell' })
    })
  }

  private bringToFront(uid: string): void {
    const current = this.bus.read(`app/${uid}/$window/z`)?.value as number | undefined
    if (current === this.topZ) return
    void this.bus.write(`app/${uid}/$window/z`, ++this.topZ, { origin: 'shell' })
  }

  /**
   * 從 `$meta` 生出一份設定表單。
   *
   * **不需要另一份 JSON。** 節點定義裡已經有全部需要的東西：`access` 決定它
   * 算不算設定、`schema` 決定要畫成什麼控制項、`description` 是說明、`unit`
   * 是單位、`example` 是提示。再寫一份「設定用的 schema」就是同一件事描述兩
   * 遍，而漂掉的一定是給人看的那一份。
   *
   * 由 shell 畫而不是 app 自己畫，有兩個理由：SDK 的網址帶版本號，第三方 app
   * 停在舊版就會有一份舊版的表單，那樣不叫統一；而 shell 的寫入帶
   * `origin: 'shell'`，閘門對它免確認（D22）——由 app 自己寫的話，人自己打的
   * 設定會被風險閘門攔下來問人。
   */
  private fillSettings(sheet: HTMLDivElement, uid: string): void {
    const meta = this.bus.read(`app/${uid}/$meta`)?.value as
      | { groups?: Record<string, string>; nodes?: NodeDef[] }
      | undefined

    sheet.replaceChildren()
    const editable = (meta?.nodes ?? []).filter(
      (n) => n.kind === 'state' && n.access === 'rw' && !n.path.startsWith('$'),
    )
    if (editable.length === 0) {
      const none = document.createElement('p')
      none.className = 'qd-info-note'
      none.textContent = this.t('noSettings')
      sheet.append(none)
      return
    }

    // 依路徑第一段分組。不靠 config/ 這個命名慣例——協定沒有規定那個前綴，
    // 靠它等於發明一條沒寫下來的規則。
    const groups = new Map<string, NodeDef[]>()
    for (const node of editable) {
      const key = node.path.split('/')[0] ?? ''
      const bucket = groups.get(key)
      if (bucket) bucket.push(node)
      else groups.set(key, [node])
    }

    const errors = document.createElement('div')
    errors.className = 'qd-set-err'

    for (const [key, nodes] of groups) {
      if (groups.size > 1) {
        const head = document.createElement('h3')
        head.className = 'qd-set-group'
        head.textContent = meta?.groups?.[key] ?? key
        sheet.append(head)
      }
      for (const node of nodes) {
        sheet.append(this.settingRow(uid, node, errors))
      }
    }
    sheet.append(errors)
  }

  /** 一列設定：標籤、控制項、說明。控制項的型別完全由 schema 推導。 */
  private settingRow(uid: string, node: NodeDef, errors: HTMLElement): HTMLElement {
    const path = `app/${uid}/${node.path}`
    const schema = node.schema as {
      type?: string
      enum?: unknown[]
      minimum?: number
      maximum?: number
      nullable?: boolean
    }
    const current = this.bus.read(path)?.value

    const row = document.createElement('div')
    row.className = 'qd-set-row'

    const label = document.createElement('label')
    label.className = 'qd-set-label'
    // 最後一段就夠了，前面那截已經是分組標題
    label.textContent = node.path.split('/').slice(1).join('/') || node.path
    if (node.unit) {
      const unit = document.createElement('span')
      unit.className = 'qd-set-unit'
      unit.textContent = node.unit
      label.append(unit)
    }

    // note 與 field 要在 commit 之前宣告：commit 是閉包，只有在使用者動了控制項
    // 才會跑，但如果宣告在它下面，number 那條提早 return 的分支會讓外層的 note
    // 永遠不存在，第一次寫入就炸在暫時死區。
    const note = document.createElement('p')
    note.className = 'qd-set-note'
    note.textContent = node.description

    const commit = (value: unknown): void => {
      void this.bus.write(path, value, { origin: 'shell' }).then((result) => {
        if (result.ok) {
          field.classList.remove('qd-bad')
          note.textContent = ''
          // 擁有者可能修正了值（例如夾到範圍內），照它說的更新
          setValue(result.value)
          return
        }
        // 拒絕的理由是擁有者給的，原樣顯示——換成「輸入錯誤」等於把它丟掉
        field.classList.add('qd-bad')
        note.textContent = result.error.message
        errors.textContent = ''
      })
    }

    let field: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    let setValue: (value: unknown) => void

    if (schema.type === 'boolean') {
      const input = document.createElement('input')
      input.type = 'checkbox'
      input.checked = current === true
      input.addEventListener('change', () => commit(input.checked))
      field = input
      setValue = (v) => {
        input.checked = v === true
      }
    } else if (Array.isArray(schema.enum)) {
      const select = document.createElement('select')
      // 值與標籤是兩件事。值進存檔、進比較，所以它不能因為換了語言就變成別的
      // 字串；標籤是人看的那一行。沒給標籤就沿用值——一支只寫給一種語言的
      // app 不必為此多做任何事。
      const labels = node.enum_labels
      for (const [i, option] of schema.enum.entries()) {
        const item = document.createElement('option')
        item.value = String(option)
        item.textContent = labels?.[i] ?? String(option)
        select.append(item)
      }
      select.value = String(current ?? '')
      select.addEventListener('change', () => commit(select.value))
      field = select
      setValue = (v) => {
        select.value = String(v ?? '')
      }
    } else if (schema.type === 'array' || schema.type === 'object') {
      // 結構化的值要當 JSON 編輯。以前這種節點會掉進最後那個文字框，送出去的是
      // 一個字串，於是必定被自己的 schema 擋下來——**設定面板畫得出來、卻怎麼
      // 填都不會過**，那比不給編輯更糟。
      const input = document.createElement('textarea')
      input.rows = 3
      input.spellcheck = false
      input.value = jsonText(current)
      if (node.example !== undefined) input.placeholder = jsonText(node.example)
      input.addEventListener('change', () => {
        const text = input.value.trim()
        if (text === '') {
          // 空白是 null，不是「空陣列」。沒設過和設成空的是兩件事。
          commit(schema.nullable ? null : text)
          return
        }
        try {
          commit(JSON.parse(text))
        } catch (error) {
          // 語法錯在送出去之前就攔下來。讓核心回一句 E_SCHEMA 也行，但那句話
          // 說的是型別不對，而真正的問題是這裡少了一個引號。
          input.classList.add('qd-bad')
          note.textContent = `${this.t('jsonBadFormat')}: ${(error as Error).message}${jsonHint(text, this.t)}`
        }
      })
      field = input
      setValue = (v) => {
        input.value = jsonText(v)
      }
    } else if (schema.type === 'number' || schema.type === 'integer') {
      const input = document.createElement('input')
      // 滑桿只在範圍**窄到拖得準**的時候才給。0.5 秒到一小時的 interval_ms
      // 兩端都有界，但一個像素約等於四秒——那不是控制項，是一條裝飾線。
      const span =
        schema.minimum !== undefined && schema.maximum !== undefined
          ? schema.maximum - schema.minimum
          : Infinity
      input.type = span <= 200 ? 'range' : 'number'
      if (schema.minimum !== undefined) input.min = String(schema.minimum)
      if (schema.maximum !== undefined) input.max = String(schema.maximum)
      if (schema.type === 'integer') input.step = '1'
      input.value = current === null || current === undefined ? '' : String(current)
      if (node.example !== undefined) input.placeholder = String(node.example)
      const readOut = document.createElement('span')
      readOut.className = 'qd-set-read'
      readOut.textContent = input.value
      input.addEventListener('input', () => {
        readOut.textContent = input.value
      })
      // change 而不是 input：拖滑桿的每一格都寫一次，會塞爆閘門與時間軸
      input.addEventListener('change', () => {
        // 可以是 null 的節點，清空就是 null，不是 0
        commit(input.value === '' && schema.nullable ? null : Number(input.value))
      })
      field = input
      setValue = (v) => {
        input.value = v === null || v === undefined ? '' : String(v)
        readOut.textContent = input.value
      }
      row.append(label, input, readOut, note)
      return row
    } else {
      const input = document.createElement('input')
      input.type = 'text'
      input.value = current === null || current === undefined ? '' : String(current)
      if (node.example !== undefined) input.placeholder = String(node.example)
      input.addEventListener('change', () => {
        commit(input.value === '' && schema.nullable ? null : input.value)
      })
      field = input
      setValue = (v) => {
        input.value = v === null || v === undefined ? '' : String(v)
      }
    }

    row.append(label, field, note)
    return row
  }

  /**
   * 把「這個工具怎麼用」畫出來。
   *
   * 內容全部來自 `$meta`——也就是 **agent 讀的同一份**。這是刻意的：說明若另外
   * 寫一份，兩份必然會漂移，而漂移的那一份一定是給人看的那份。人看得到的東西
   * 就是機器看得到的東西，這條原則在這裡是字面上成立的。
   */
  private fillInfo(sheet: HTMLDivElement, uid: string): void {
    const meta = this.bus.read(`app/${uid}/$meta`)?.value as
      | { summary?: string; nodes?: { path: string; kind: string; access: string; description?: string; unit?: string; risk?: string }[] }
      | undefined

    sheet.replaceChildren()
    if (!meta) {
      const wait = document.createElement('p')
      wait.className = 'qd-info-note'
      wait.textContent = this.t('notReady')
      sheet.append(wait)
      return
    }

    if (meta.summary) {
      const lede = document.createElement('p')
      lede.className = 'qd-info-lede'
      lede.textContent = meta.summary
      sheet.append(lede)
    }

    const nodes = (meta.nodes ?? []).filter((n) => !n.path.startsWith('$'))
    if (nodes.length === 0) {
      const none = document.createElement('p')
      none.className = 'qd-info-note'
      none.textContent = this.t('noTopics')
      sheet.append(none)
      return
    }

    for (const node of nodes) {
      const row = document.createElement('div')
      row.className = 'qd-info-row'

      const head = document.createElement('div')
      head.className = 'qd-info-head'
      const path = document.createElement('code')
      path.textContent = node.path.replace(`app/${uid}/`, '')
      const tag = document.createElement('span')
      tag.className = 'qd-info-tag'
      // 讀寫比 kind 更能回答「我可以拿它做什麼」，所以放在前面
      tag.textContent = this.t(
      node.access === 'r' ? 'readOnly' : node.kind === 'action' ? 'callable' : 'writable',
    )
      head.append(path, tag)
      if (node.unit) {
        const unit = document.createElement('span')
        unit.className = 'qd-info-unit'
        unit.textContent = node.unit
        head.append(unit)
      }
      if (node.risk && node.risk !== 'none') {
        const risk = document.createElement('span')
        risk.className = 'qd-info-risk'
        risk.textContent = this.t('needsConfirm')
        head.append(risk)
      }
      row.append(head)

      if (node.description) {
        const desc = document.createElement('p')
        desc.className = 'qd-info-desc'
        desc.textContent = node.description
        row.append(desc)
      }
      sheet.append(row)
    }
  }

  /**
   * 移到底層。
   *
   * 不能只寫「最小的 z 減一」：`.qd-root` 沒有建立堆疊脈絡，負的 z-index 會讓
   * 視窗掉到桌布**後面**去，等於消失。所以改成重新編號——把所有視窗依現在的
   * 順序排成 1..n，這一個排最前面。n 很小，一次全寫也不痛。
   */
  private sendToBack(uid: string): void {
    const order = [...this.windows.keys()].sort((a, b) => this.zOf(a) - this.zOf(b))
    const rest = order.filter((id) => id !== uid)
    if (rest.length === 0) return

    const next = [uid, ...rest]
    next.forEach((id, index) => {
      const z = index + 1
      if (this.zOf(id) !== z) {
        void this.bus.write(`app/${id}/$window/z`, z, { origin: 'shell' })
      }
    })
    this.topZ = next.length
  }

  private zOf(uid: string): number {
    const z = this.bus.read(`app/${uid}/$window/z`)?.value
    return typeof z === 'number' ? z : 0
  }

  // ─────────────────────────────── topic → 畫面

  private wireTopics(): void {
    this.bus.subscribe('$sys/wallpaper/color', (m) => {
      // 桌布是螢幕的，不是桌面的：桌面捲動的時候桌布不跟著跑，就跟真的一樣。
      if (m.type === 'state') this.screen.style.background = String(m.value)
    })

    this.bus.subscribe('$sys/registry/instances', (m) => {
      if (m.type !== 'state') return
      for (const instance of m.value as InstanceSummary[]) {
        const view = this.windows.get(instance.uid)
        if (view) view.titleText.textContent = instance.title
      }
    })

    this.bus.subscribe('app/+/$window/rect', (m) => {
      if (m.type !== 'state') return
      const view = this.viewFor(m.path)
      if (!view) return
      const rect = m.value as WindowRect
      view.frame.style.left = `${rect.x}px`
      view.frame.style.top = `${rect.y}px`
      view.frame.style.width = `${rect.w}px`
      view.frame.style.height = `${rect.h}px`
    })

    this.bus.subscribe('app/+/$window/z', (m) => {
      if (m.type !== 'state') return
      const view = this.viewFor(m.path)
      if (!view) return
      const z = m.value as number
      view.frame.style.zIndex = String(z)
      if (z > this.topZ) this.topZ = z
    })

    this.bus.subscribe('app/+/$window/state', (m) => {
      if (m.type !== 'state') return
      const view = this.viewFor(m.path)
      if (!view) return
      const state = String(m.value)
      // hidden 的 app 仍然執行（無頭 app 就是這樣），只是不畫出來。
      // min 也不畫視窗，但**會在底部留一顆膠囊**——最小化的東西要找得回來，
      // 不然它和 hidden 就沒有分別，而那兩件事不一樣。
      view.frame.style.display = state === 'hidden' || state === 'min' ? 'none' : 'flex'
      this.renderDock()
      // 最大化不動 rect，只是覆蓋呈現，所以還原時自然回到原本的位置
      view.frame.classList.toggle('qd-max', state === 'max')
      view.maximise.textContent = state === 'max' ? '❐' : '▢'
      view.maximise.title = this.t(state === 'max' ? 'restore' : 'maximise')
    view.maximise.dataset['i18nTitle'] = state === 'max' ? 'restore' : 'maximise'
    })

    this.bus.subscribe('$sys/layout/templates', (m) => {
      if (m.type === 'state') this.renderGuides()
    })

    this.bus.subscribe('$sys/layout/template', (m) => {
      if (m.type !== 'state') return
      this.renderGuides()
      // 剛選好板型時閃一下，讓人看清楚版面長什麼樣
      if (!m.retained && m.value !== 'free') this.flashGuides()
    })

    this.bus.subscribe('app/+/$window/locked', (m) => {
      if (m.type !== 'state') return
      const view = this.viewFor(m.path)
      if (!view) return
      // 拖曳與縮放的把手收起來。真正的強制在核心——這裡只是別讓人白拖一場。
      view.frame.classList.toggle('qd-pinned', m.value === true)
      // dock 上那顆 📌 要跟著更新數字，清單開著的話也要重畫
      this.renderDock()
      if (this.pinList) this.fillPinList(this.pinList)
    })

    this.bus.subscribe('app/+/$window/transparent', (m) => {
      if (m.type !== 'state') return
      const view = this.viewFor(m.path)
      if (!view) return
      // 只移除桌面畫的底色；app 自己的頁面背景若不透明仍然會擋住
      view.frame.classList.toggle('qd-transparent', m.value === true)
    })


    this.bus.subscribe('app/+/$lifecycle', (m) => {
      if (m.type !== 'state') return
      const view = this.viewFor(m.path)
      if (!view) return
      const lifecycle = String(m.value)
      view.statusText.textContent = lifecycle
      view.statusText.hidden = lifecycle === 'ready'
      const dead = lifecycle === 'crashed' || lifecycle === 'closed'
      view.tombstone.style.display = dead ? 'flex' : 'none'
      const label = view.tombstone.firstElementChild
      if (label) {
        label.textContent = this.t(lifecycle === 'crashed' ? 'noResponse' : 'ended')
      }
    })

    // 實例被完全移除（$sys/cmd/close）時，$meta 會被撤下
    this.bus.subscribe('app/+/$meta', (m) => {
      if (m.type !== 'retract') return
      const uid = m.path.split('/')[1]
      if (!uid) return

      // 系統服務被關掉就重新起來——不然「重設桌面」會永久消失。
      // 這是 supervisor 的行為，不是禁止使用者關它。
      if (uid === SESSION_APP_UID) {
        this.sessionApp = null
        queueMicrotask(() => this.mountSessionApp())
        return
      }

      const view = this.windows.get(uid)
      if (!view) return
      view.binding.dispose()
      view.frame.remove()
      this.windows.delete(uid)
      this.renderDock()
      if (this.pinList) this.fillPinList(this.pinList)
    })
  }

  /**
   * shell 認得的能力名稱。
   *
   * 一份白名單，不是照單全收：目錄裡的字串會變成 iframe 的 `allow` 屬性，而一
   * 個不在這裡的名字要嘛是打錯（靜靜地沒有作用），要嘛是有人想塞別的東西進那
   * 個屬性。兩種都該被擋下來並說出來。
   *
   * 這份清單刻意短。要多一個能力，就是有人想清楚了為什麼——那正是它該有的成本。
   */
  /**
   * 這個網址的 app 獲准向瀏覽器要求什麼。
   *
   * **從目錄查，不從 spec 拿。** spec 可能來自存檔，而存檔是使用者那一側的資
   * 料——讓它決定權限，等於讓一份被改過的快照給自己開麥克風。目錄是宿主的檔
   * 案，所以它才是這件事的來源；shell 拿網址回去對，對不上就什麼都不給。
   *
   * `allow="microphone"` 的意思是把這個能力委派給該 iframe 的來源。委派本身
   * 不繞過任何人：瀏覽器仍然會問使用者。少了它，跨來源的 app 連問都問不到，
   * 而且 getUserMedia 會直接被拒——那個失敗很安靜，看起來像 app 自己寫壞了。
   */
  /**
   * 要載進 iframe 的網址。
   *
   * 目錄裡的網址是**建置時**寫死的，而元件需要知道兩件事：**SDK 在哪裡**（那是
   * 一份公開的程式碼，目錄裡那個網址就對了），以及**誰是它的 parent**——只有那
   * 個來源交過來的 Port 才算數。
   *
   * 第二件事建置時不可能知道。原生殼裡桌面跑在它自己的來源上（Android 是
   * https://qdesktop.local，iOS 是 capacitor://qdesktop.local），對不上的時候
   * SDK **一句話都不會說**：它把那個 port 訊息丟掉，十秒後逾時，畫面上是每一個
   * 元件都停在「載入中」，看起來像網路慢。
   *
   * 所以由 shell 在載入的當下補上 `parent`。**只補這一個**：曾經是連 `desktop`
   * 一起改寫的，那讓元件改去本機來源載 SDK，於是 Android 那側得補 CORS 標頭才
   * 載得到——而 iOS 沒有那個鉤子。一個參數兼兩份差事，兩個平台就得各自想辦法。
   *
   * 網址壞掉就原樣放行——那是目錄的問題，不該在這裡變成另一個症狀。
   */
  private frameUrl(url: string): string {
    try {
      const at = new URL(url, location.href)
      at.searchParams.set('parent', location.origin)
      return at.href
    } catch {
      return url
    }
  }

  private permissionsFor(url: string): string {
    const entry = this.bus.getCatalog().find((candidate) => candidate.url === url)
    return grantedPermissions(entry, (name) => {
      // 說出來。一個被丟掉的權限，症狀是那個 app 的某個功能安靜地不能用。
      console.warn(`[QDesktop] 目錄裡有不認得的權限：${name}（${entry?.app_id ?? url}）`)
    })
  }

  private viewFor(path: string): WindowView | undefined {
    const uid = path.split('/')[1]
    return uid ? this.windows.get(uid) : undefined
  }

  /**
   * 右鍵選單：搜尋並開啟一個工具。
   *
   * 它以前是一個 app，搬進來是因為它一直在跟視窗系統打架——選單是**桌面層級
   * 的東西**：要用桌面座標定位、要蓋在所有視窗之上、要無邊框、要立刻拿到鍵盤。
   * 當成視窗做，就會頂著一條標題列、被自己 iframe 的寬度夾住位置、還搶不到焦點。
   * 那三個症狀不是實作粗糙，是它放錯層。
   *
   * 它通過 D17 的判準：整段程式碼裡沒有任何一個 app 的名字，只讀
   * `$sys/registry/apps` 再呼叫 `$sys/cmd/launch`。shell 不因此多認得誰。
   */
  private openMenu(at: { x: number; y: number }): void {
    this.closeMenu()

    // 隱形遮罩:先鋪一片吃掉外面(桌布、iframe)的第一次點擊,直接關選單。
    // iframe 內部的 pointerdown 不會冒泡到父 document,單靠 focusin 抓不到,
    // 尤其某些 WKWebView 版本 focusin 根本不 fire——這一層就是為了那條缺口。
    const scrim = document.createElement('div')
    scrim.className = 'qd-scrim'
    scrim.addEventListener('pointerdown', (event) => {
      event.stopPropagation()
      this.dismissMenu()
    })
    this.screen.append(scrim)
    this.menuScrim = scrim

    const menu = document.createElement('div')
    menu.className = 'qd-menu'

    const search = document.createElement('input')
    search.type = 'text'
    search.placeholder = this.t('searchTools')
    search.autocomplete = 'off'
    search.spellcheck = false

    const list = document.createElement('ul')
    menu.append(search, list)
    this.screen.append(menu)
    this.menu = menu

    /*
      Tap 走 pointerdown/pointerup 加事件委派，不用 `click`——`click` 在 iOS
      Safari / WKWebView 的 scrollable container 裡對非 <button>/<a> 元素判定
      挺挑的：有時候手指落定、抬起，click 就是不 fire。pointerup 是低階事件，
      幾乎一定會來。

      判斷路徑：
        - pointerdown 記下起點 li 和位置
        - pointerup 位移 < 6px 且還停在同一顆 li 上 → tap，launch
        - 位移大過閾值 → 當滾動，不動作
        - pointercancel → 中斷（瀏覽器啟動了滾動）→ 不動作
    */
    const TAP_THRESHOLD = 6
    let tapOriginX = 0
    let tapOriginY = 0
    let tapItem: HTMLLIElement | null = null
    list.addEventListener('pointerdown', (event) => {
      const li = (event.target as Element).closest('li')
      if (!li || !list.contains(li)) return
      tapOriginX = event.clientX
      tapOriginY = event.clientY
      tapItem = li as HTMLLIElement
    })
    list.addEventListener('pointerup', (event) => {
      const startedOn = tapItem
      tapItem = null
      if (!startedOn) return
      const dx = event.clientX - tapOriginX
      const dy = event.clientY - tapOriginY
      if (dx * dx + dy * dy > TAP_THRESHOLD * TAP_THRESHOLD) return
      const endedOn = (event.target as Element).closest('li')
      if (endedOn !== startedOn) return
      const idx = Array.from(list.children).indexOf(startedOn)
      // 取出來再判斷，不是先判斷範圍再取：noUncheckedIndexedAccess 認的是**這一次
      // 取值**的結果，不是上一行的推論。而且它涵蓋得更完整——indexOf 找不到時回
      // 傳 -1，那一格本來就是 undefined。
      const picked = shown[idx]
      if (!picked) return
      this.launchFromMenu(picked, at)
    })
    list.addEventListener('pointercancel', () => {
      tapItem = null
    })

    // 夾在桌面裡面，用的是**桌面**的尺寸。以前那個 app 讀的是自己 iframe 的
    // innerWidth，於是永遠夾成左上角——選單放錯層的代價之一。
    // at 是**畫布座標**（右鍵按在畫布上的那一點），而選單掛在 root 上並用 root
    // 座標定位——畫布跟視窗一樣大時兩者重合，置中縮小之後就差一個位移。所以先
    // 換算回來，再夾進視窗範圍。
    //
    // 選單留在 root 上而不是搬進畫布：畫布那一層會裁切，而一個開在畫布邊緣的
    // 選單會被切掉一半。它是視窗層級的東西，不該跟著內容捲。
    const box = this.screen.getBoundingClientRect()
    const canvasBox = this.canvas.getBoundingClientRect()
    const x = at.x + canvasBox.left - box.left
    const y = at.y + canvasBox.top - box.top
    const size = menu.getBoundingClientRect()
    menu.style.left = `${Math.max(8, Math.min(x, box.width - size.width - 8))}px`

    /*
      下緣要夾到**鍵盤上面**，不是視窗下緣。

      這個選單一開就把焦點交給搜尋框，所以手機上鍵盤必然跟著上來——而鍵盤蓋住
      的正是它自己。夾在 `box.height` 的話，選單停在鍵盤底下，看起來像按了沒反應。

      visualViewport 是「現在真的看得到多高」，鍵盤升起就會變小。原生殼刻意不讓
      鍵盤改變 WebView 的尺寸（那會讓整片桌面的視窗重排），所以那個高度是唯一問
      得到答案的地方。沒有這個 API 的環境就退回視窗高度，行為跟以前一樣。
    */
    const place = (): void => {
      // 鍵盤吃掉多少。**宿主說了算**：原生殼裡 visualViewport 不會縮（那個殼刻意
      // 不讓鍵盤改變 WebView 的尺寸，否則整片桌面的視窗會重排），所以那個 API 回
      // 報的仍然是全高，而選單就被排到鍵盤底下。
      //
      // 用一個 CSS 變數而不是問 Capacitor：shell 對宿主是什麼一無所知（§9），而
      // 「還剩多少高度可用」本來就是宿主才答得出來的事。沒有人設就退回
      // visualViewport，瀏覽器上那個是準的。
      const declared = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--qd-keyboard'),
      )
      const eaten = Number.isFinite(declared) && declared > 0 ? declared : 0
      const seen = eaten > 0 ? box.height - eaten : (globalThis.visualViewport?.height ?? box.height)
      const room = Math.min(box.height, seen)
      const height = menu.getBoundingClientRect().height || size.height
      menu.style.top = `${Math.max(8, Math.min(y, room - height - 8))}px`
    }
    place()
    // 鍵盤升起、降下、換輸入法都會改變那個高度，所以是訂閱不是讀一次。宿主改
    // --qd-keyboard 時也要重排，而那件事沒有事件可訂——所以連 transitionend 之類
    // 的都不靠，直接讓宿主在改完之後派一個 resize。
    const viewport = globalThis.visualViewport
    viewport?.addEventListener('resize', place)
    addEventListener('resize', place)
    // 點到 iframe（元件內容）也要收選單。iframe click **不會 bubble** 到父
    // 文件（跨來源安全政策），所以上面 `.qd-root pointerdown` 那條 listener
    // 抓不到。兩條備援：
    //
    //   1. `focusin` on document — 一 iframe 被點就會拿 focus，parent 這邊
    //      `focusin` 冒泡出來，target 是那顆 iframe（一定不在 .qd-menu 裡）。
    //      這條是主要防線，跟 WKWebView 各種 focus 行為都撐得住。
    //   2. `window.blur` — 補洞用：window 整體失焦（點進 iframe 是其中一種
    //      觸發），也收。有的 WKWebView build focusin 沒 bubble 就靠這條。
    //
    // 兩條都用 setTimeout 拉到下一 tick 掛，避開選單開起來瞬間 search.focus
    // 觸發的短暫 focus 抖動——不然剛開就當場關。
    const dismissOnFocusIn = (event: FocusEvent): void => {
      const target = event.target as Element | null
      if (target?.closest?.('.qd-menu')) return
      this.dismissMenu()
    }
    const dismissOnBlur = (): void => this.dismissMenu()
    const attachAway = setTimeout(() => {
      document.addEventListener('focusin', dismissOnFocusIn, true)
      addEventListener('blur', dismissOnBlur)
    }, 0)
    this.menuCleanup = () => {
      viewport?.removeEventListener('resize', place)
      removeEventListener('resize', place)
      clearTimeout(attachAway)
      document.removeEventListener('focusin', dismissOnFocusIn, true)
      removeEventListener('blur', dismissOnBlur)
    }

    const catalog = (this.bus.read('$sys/registry/apps')?.value as CatalogEntry[]) ?? []
    let shown: CatalogEntry[] = []
    let cursor = 0

    const render = (): void => {
      const q = search.value.trim().toLowerCase()
      shown = q
        ? catalog.filter(
            (a) =>
              a.title.toLowerCase().includes(q) ||
              a.app_id.toLowerCase().includes(q) ||
              (a.summary ?? '').toLowerCase().includes(q),
          )
        : catalog
      if (cursor >= shown.length) cursor = Math.max(0, shown.length - 1)

      list.replaceChildren()
      if (shown.length === 0) {
        const none = document.createElement('li')
        none.className = 'qd-menu-none'
        none.textContent = q
        ? this.locale === 'en'
          ? `No match for “${search.value.trim()}”`
          : `找不到「${search.value.trim()}」`
        : this.t('catalogEmpty')
        list.append(none)
        return
      }
      shown.forEach((app, i) => {
        const item = document.createElement('li')
        item.setAttribute('aria-selected', String(i === cursor))
        const name = document.createElement('b')
        name.textContent = app.title
        const sum = document.createElement('span')
        sum.textContent = app.summary ?? ''
        item.append(name, sum)
        // launch 走 ul 上的事件委派（見上面 pointerdown/pointerup）。這裡只
        // 管 hover 高亮，而且**只切 aria-selected**，不呼叫 render()——render 會
        // replaceChildren() 把整份 li 換掉。觸控上 pointerenter 恰巧在 pointerdown
        // 之後、pointerup 之前 fire，那次重建會讓 pointerdown 記下的 li 節點瞬間
        // 變成脫離 DOM 的舊物件，pointerup 拿到的是同索引的新節點，`endedOn !==
        // startedOn` 就 return——症狀是「選有亮但按不下去」。這裡改成 in-place 修
        // 改 attribute，DOM 節點保留，事件委派那條就吃得到。
        item.addEventListener('pointerenter', () => {
          if (cursor === i) return
          const prev = list.children[cursor] as HTMLElement | undefined
          prev?.setAttribute('aria-selected', 'false')
          cursor = i
          item.setAttribute('aria-selected', 'true')
        })
        list.append(item)
      })
      list.children[cursor]?.scrollIntoView({ block: 'nearest' })
    }

    search.addEventListener('input', () => {
      cursor = 0
      render()
    })
    search.addEventListener('keydown', (event) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          cursor = Math.min(cursor + 1, shown.length - 1)
          render()
          break
        case 'ArrowUp':
          event.preventDefault()
          cursor = Math.max(cursor - 1, 0)
          render()
          break
        case 'Enter': {
          event.preventDefault()
          const chosen = shown[cursor]
          if (chosen) this.launchFromMenu(chosen, at)
          break
        }
        case 'Escape':
          event.preventDefault()
          this.dismissMenu()
          break
      }
    })

    render()
    // 在 shell 自己的 DOM 裡，所以 focus 直接生效——不必跨 iframe 搶焦點
    search.focus()
  }

  /**
   * 從選單開一個工具，**放在按右鍵的那個位置**。
   *
   * 只給 x/y，尺寸沿用目錄的預設——「放這裡」講的是位置，不是大小。走的是
   * 跟 agent 完全相同的那條路（`$sys/cmd/launch` 的擺放參數），所以人指定位置
   * 與 agent 指定位置不是兩套機制。
   */
  private launchFromMenu(app: CatalogEntry, at: { x: number; y: number }): void {
    this.dismissMenu()
    void this.bus.call(
      '$sys/cmd/launch',
      { app_id: app.app_id, rect: { x: at.x, y: at.y } },
      { origin: 'shell' },
    )
  }

  private closeMenu(): void {
    this.menu?.remove()
    this.menu = null
    this.menuScrim?.remove()
    this.menuScrim = null
    // 解掉 visualViewport 的訂閱。留著的話每開一次選單就多一個，而它們指向的
    // 是已經被移除的元素——不會壞，只是每次都白算一遍，而且越來越多。
    this.menuCleanup?.()
    this.menuCleanup = null
  }

  /** 收起來，並把 `$sys/menu/at` 清掉——不清的話下次同一個位置寫不進去。 */
  private dismissMenu(): void {
    this.closeMenu()
    void this.bus.write('$sys/menu/at', null, { origin: 'shell' })
  }

  /**
   * 底部的膠囊列：每個最小化的視窗一顆，點一下回來。
   *
   * 沒有這個，`min` 就只是另一種 `hidden`——視窗消失，而且只能繞去啟動器找。
   * 兩者的差別本來就寫在 `$window/state` 的說明裡（hidden 是「在跑但不顯示」），
   * 這裡只是把那個差別畫出來。
   */
  /**
   * 左上角那顆點。
   *
   * 只認四種狀態的字面值,其餘照原樣顯示——shell 不該假設宿主的詞彙表只有這幾
   * 個,而一個顯示著陌生字串的燈,仍然比一個什麼都不說的燈有用。
   */
  /** 左上角那一排。連線與存檔住在同一個盒子裡,所以它們不必各自算位置。 */
  private status(): HTMLDivElement {
    if (!this.statusBar) {
      this.statusBar = document.createElement('div')
      this.statusBar.className = 'qd-status'
      this.screen.append(this.statusBar)
    }
    return this.statusBar
  }

  /**
   * 存好了沒。
   *
   * 它以前只活在設定面板裡,而把面板關掉是很普通的事——於是「有沒有存起來」這
   * 個問題,只有在你剛好開著那扇窗的時候才回答得出來。它跟連線是同一類問題,所
   * 以放在同一排。
   *
   * 沒有 store 的部署完全不顯示:一台不同步的桌面沒有「未儲存」可言,而一個永
   * 遠停在某個狀態的指示燈,會讓人以為那台桌面壞了。
   */
  private renderSave(): void {
    if (!this.saveBadge) {
      this.saveBadge = document.createElement('div')
      this.saveBadge.className = 'qd-save'
      this.status().append(this.saveBadge)
    }
    this.saveBadge.hidden = this.saveState === ''
    if (this.saveState === '') return
    const key: Record<string, ShellKey> = {
      saved: 'saveSaved',
      saving: 'saveSaving',
      error: 'saveError',
    }
    this.saveBadge.dataset['state'] = this.saveState
    // 失敗的理由放在 title 而不是攤在膠囊上:那句話可能很長,而那裡是一排狀
    // 態,不是一個訊息區。滑過去看得到,而且它同時進了 data/save_error。
    this.saveBadge.title =
      this.saveState === 'error' ? this.saveError : this.saveReason ? `↻ ${this.saveReason}` : ''
    // 滑得到才問得出來。膠囊本來是 pointer-events:none（它不擋點擊），但一個
    // 說不出理由的狀態燈，在出事的時候等於沒有。
    this.saveBadge.style.pointerEvents = 'auto'
    this.saveBadge.replaceChildren(
      document.createElement('i'),
      document.createTextNode(this.t(key[this.saveState] as ShellKey)),
    )
  }

  private setSaveState(next: '' | 'saved' | 'saving' | 'error', error = ''): void {
    if (next === this.saveState && error === this.saveError) return
    this.saveState = next
    this.saveError = error
    this.renderSave()
  }

  private renderLink(): void {
    if (!this.link) {
      this.link = document.createElement('div')
      this.link.className = 'qd-link'
      // prepend：連線在左、存檔在右。兩者誰先建出來看時序，而排列順序不該跟著
      // 時序跑——一排會換位置的狀態燈，看的人得每次重新找。
      this.status().prepend(this.link)
    }
    const known: Record<string, ShellKey> = {
      connected: 'linkConnected',
      connecting: 'linkConnecting',
      offline: 'linkOffline',
      unpaired: 'linkUnpaired',
    }
    const key = known[this.linkState]
    this.link.dataset['state'] = this.linkState
    this.link.replaceChildren(
      document.createElement('i'),
      document.createTextNode(key ? this.t(key) : this.linkState),
    )
  }

  /**
   * 那顆「新增」。它做的事跟在桌布上按右鍵**完全一樣**——寫 `$sys/menu/at`。
   *
   * 不是另開一條路：選單本來就是那條 topic 的視圖，所以人按右鍵、按這顆、還是
   * 代理人寫那個節點，走的是同一段程式。多一個入口不該多一份行為。
   */
  /**
   * 讓 dock 浮現 + 排程再淡出。任何觸控都會 bump 一次——閒置 3 秒後淡出，之
   * 後只要再有互動就重新浮現。永不隱藏的作法（原本）在小螢幕上把 dock 那條
   * 一直壓在下面、擋內容；閒置隱藏之後閱讀更乾淨、要用時再現。
   */
  /** dock 位置/方向的 localStorage key，**依當下的 desktop_id 而分**——不同
   *  桌面上元件不一樣，最順手的 dock 位置也不一樣，共用一份等於每換一張桌面
   *  dock 就跑到擋內容的地方。沒 account 或沒 desktop_id 時（web/demo）走
   *  `_default` sentinel，同一份設定跟著這台裝置。 */
  private dockScope(): string {
    return this.account?.desktop_id || '_default'
  }
  private dockPosKey(): string {
    return `qd:dock:pos:${this.dockScope()}`
  }
  private dockVerticalKey(): string {
    return `qd:dock:vertical:${this.dockScope()}`
  }

  private bumpDock(): void {
    const dock = this.dock
    if (!dock) return
    dock.classList.remove('qd-hidden')
    if (this.dockHideTimer !== null) clearTimeout(this.dockHideTimer)
    this.dockHideTimer = setTimeout(() => {
      dock.classList.add('qd-hidden')
      this.dockHideTimer = null
    }, 3000)
  }

  private renderDock(): void {
    let dock = this.dock
    if (!dock) {
      dock = document.createElement('div')
      dock.className = 'qd-dock'
      this.screen.append(dock)
      this.dock = dock
    }
    dock.replaceChildren()

    // 兩顆控制 dock 自己形狀的膠囊——把手 + 轉向。永遠是最前面兩顆，這樣
    // 直式時它們在最上、橫式時在最左，「手拿把手拖、按方向切換」的直覺不
    // 隨方向變。
    const dragBtn = document.createElement('button')
    dragBtn.className = 'qd-pill qd-dock-drag'
    dragBtn.textContent = '⋮⋮'
    dragBtn.title = this.locale === 'en' ? 'Drag to move dock' : '拖曳搬動 dock'
    dragBtn.setAttribute('aria-label', dragBtn.title)
    this.wireDockDrag(dragBtn)
    dock.append(dragBtn)

    const orientBtn = document.createElement('button')
    orientBtn.className = 'qd-pill qd-dock-orient'
    // Icon 指向「按下去會變成什麼」：橫式時顯示 ⇵（意即「變直式」），直式時
    // 顯示 ⇔（意即「變橫式」）。
    orientBtn.textContent = this.dockVertical ? '⇔' : '⇵'
    orientBtn.title = this.locale === 'en'
      ? this.dockVertical ? 'Switch to horizontal' : 'Switch to vertical'
      : this.dockVertical ? '切為橫式' : '切為直式'
    orientBtn.setAttribute('aria-label', orientBtn.title)
    orientBtn.addEventListener('click', (event) => {
      event.stopPropagation()
      this.dockVertical = !this.dockVertical
      try { localStorage.setItem(this.dockVerticalKey(), this.dockVertical ? 'true' : 'false') } catch {}
      this.applyDockLayout()
      // 標籤跟提示要跟著改；直接重畫最省事，順帶把可能因為方向變化而位移到
      // 螢幕外的 dock 拉回來（applyDockLayout 會 clamp）。
      this.renderDock()
    })
    dock.append(orientBtn)

    for (const [uid, view] of this.windows) {
      const snapshot = this.bus.read(`app/${uid}/$window/state`)
      if (snapshot?.value !== 'min') continue
      const pill = document.createElement('button')
      pill.className = 'qd-pill'
      pill.textContent = view.titleText.textContent ?? uid
      pill.title =
        this.locale === 'en' ? `Restore “${pill.textContent}”` : `還原「${pill.textContent}」`
      pill.addEventListener('click', () => {
        void this.bus.write(`app/${uid}/$window/state`, 'normal', { origin: 'shell' })
        // 拉回來就該在最上面，否則點了像沒反應
        void this.bus.write(`app/${uid}/$window/z`, ++this.topZ, { origin: 'shell' })
      })
      dock.append(pill)
    }

    // 誰把這台桌面解鎖的。**永遠在**——兩台桌面長得一模一樣，但它們可能屬於
    // 不同的人、配在不同的工作空間，而那正是「代理人碰不到我的桌面」最常見的
    // 原因。沒有登入閘時什麼都不畫。單顆膠囊，tap 走 `onSwitchDesktop`——不管
    // web 還是原生 app 都同一條路（原生 app 由 host 決定跳回哪個 picker，跟
    // 這裡沒關係）。
    if (this.account) {
      this.renderDesktopChip(dock)
    }

    // 新增元件的入口——本來是右下角一顆浮動的圓 `+`，跟 dock 分成兩處，
    // 淡出隱藏之後兩個地方都得摸一次。挪到 dock 裡當一顆膠囊，跟釘住按鈕併
    // 排，「有互動時整條浮出」一次涵蓋兩顆。位置的計算沿用 menu 開在按鈕左
    // 上方那條路——按鈕在 dock 裡也算得到 rect。
    const addBtn = document.createElement('button')
    addBtn.className = 'qd-pill qd-addbtn'
    addBtn.textContent = '＋'
    addBtn.title = this.t('addWidget')
    addBtn.setAttribute('aria-label', this.t('addWidget'))
    addBtn.addEventListener('click', (event) => {
      event.stopPropagation()
      const at = addBtn.getBoundingClientRect()
      const box = this.canvas.getBoundingClientRect()
      void this.bus.write(
        '$sys/menu/at',
        { x: Math.round(at.left - box.left - 200), y: Math.round(at.top - box.top - 280) },
        { origin: 'shell' },
      )
    })
    dock.append(addBtn)

    // 說明文件的入口。網頁版永遠在——第一次進來的人這裡是唯一有把手可以拉
    // 的東西。原生 app 把 `showHelp` 傳 false 關掉：Flutter 那邊「帳號 → 說明
    // 文件」就是同一份東西，dock 上再一顆會功能重複。
    if (this.showHelp) {
      const helpBtn = document.createElement('button')
      helpBtn.className = 'qd-pill qd-helpbtn'
      helpBtn.textContent = '?'
      helpBtn.title = this.t('help')
      helpBtn.setAttribute('aria-label', this.t('help'))
      helpBtn.addEventListener('click', (event) => {
        event.stopPropagation()
        this.openHelp()
      })
      dock.append(helpBtn)
    }

    // 釘住的入口。**永遠在**（只要桌面上還有視窗），因為釘與解是同一顆按鈕：
    // 標題列上放不得——釘下去標題列自己就收起來了，那個開關只有開沒有關。
    //
    // 為什麼是 dock 而不是右鍵選單或桌面設定：跨來源 iframe 會把右鍵和鍵盤
    // 全部吃掉，一個釘住又最大化的視窗會蓋掉整片桌布，連選單都叫不出來。
    // dock 是 shell 自己的元素、疊在所有視窗之上，是唯一永遠碰得到的表面。
    const live = this.pinnable()
    if (live.length > 0) {
      const pinned = live.filter(([uid]) => this.isPinned(uid)).length
      const pinBtn = document.createElement('button')
      pinBtn.className = `qd-pill qd-pinbtn${pinned > 0 ? ' qd-on' : ''}`
      pinBtn.textContent = pinned > 0 ? `🔒 ${pinned}` : '🔒'
      pinBtn.title =
      pinned > 0
        ? this.locale === 'en'
          ? `${pinned} window${pinned === 1 ? '' : 's'} locked`
          : `已鎖住 ${pinned} 個視窗`
        : this.t('lockWindows')
      pinBtn.addEventListener('click', (event) => {
        event.stopPropagation()
        if (this.pinList) this.closePinList()
        else this.openPinList(pinBtn)
      })
      dock.append(pinBtn)
    } else {
      this.closePinList()
    }

    // 拖曳把手 + 轉向按鈕永遠在，dock 永遠不是「空的」。qd-empty 只是舊路徑
    // 留下的保險——留著不動也不會誤觸。
    dock.classList.toggle('qd-empty', dock.childElementCount === 0)
    // 每次 render 完套一次 layout：位置（left/top）跟方向（vertical class）
    // 都是 dock 自身的狀態，跟內容變化沒關係。放在最後確保尺寸拿得到、才
    // 能正確 clamp 到螢幕範圍。
    this.applyDockLayout()
    // 任何時候 dock 內容變了，順手浮一次——狀態才剛動就先讓人看到，例如
    // 新視窗被最小化、剛切到有東西可以釘的畫面。閒置計時器隨之重新開始。
    this.bumpDock()
  }

  /**
   * 把 `dockPos` / `dockVertical` 套到 DOM 上。渲染跟位置解耦：`renderDock`
   * 只管內容，位置狀態由這裡處理，避免每次視窗開關都要重算浮動座標。
   *
   * 邊界處理：把 dock 拉回螢幕內（clamp 到 `screen` bounds）。方向切換之後
   * dock 尺寸會變，同一組 `dockPos` 可能就出界；拉回來比不畫要好。
   *
   * 直式強制轉為 floating——`bottom:0;left:0;right:0` 加 `flex-direction:column`
   * 會撐出一根貼底、寬到滿螢幕、只有幾顆按鈕的直條，一定不是使用者想要的。
   */
  private applyDockLayout(): void {
    const dock = this.dock
    if (!dock) return
    try {
      dock.classList.toggle('qd-dock-vertical', this.dockVertical)
      // 直式沒儲存位置就給一個合理預設：右邊三分之一高、離右邊 12px。若 screen
      // 還沒佈好（寬高 0），寧可用固定安全值也不要 (8, 8)——後者會蓋掉左上角
      // 連線膠囊那條 status bar。
      if (this.dockVertical && !this.dockPos) {
        const screenRect = this.screen.getBoundingClientRect()
        const w = screenRect.width > 0 ? screenRect.width : 320
        const h = screenRect.height > 0 ? screenRect.height : 600
        const estW = 200
        this.dockPos = {
          x: Math.max(60, Math.round(w - estW - 12)),
          y: Math.max(60, Math.round(h / 3)),
        }
        try { localStorage.setItem(this.dockPosKey(), JSON.stringify(this.dockPos)) } catch {}
      }
      if (this.dockPos) {
        // Clamp 到目前螢幕範圍——旋轉、resize、換裝置都可能讓上次記的座標出界。
        const screenRect = this.screen.getBoundingClientRect()
        const dockRect = dock.getBoundingClientRect()
        const maxX = Math.max(0, screenRect.width - dockRect.width)
        const maxY = Math.max(0, screenRect.height - dockRect.height)
        const x = Math.max(0, Math.min(maxX, this.dockPos.x))
        const y = Math.max(0, Math.min(maxY, this.dockPos.y))
        // 最後一道防線——上面任何一步意外產生 NaN 就走預設位置，別套 NaNpx。
        if (Number.isFinite(x) && Number.isFinite(y)) {
          dock.classList.add('qd-dock-floating')
          dock.style.left = `${x}px`
          dock.style.top = `${y}px`
        } else {
          dock.classList.remove('qd-dock-floating')
          dock.style.left = ''
          dock.style.top = ''
        }
      } else {
        dock.classList.remove('qd-dock-floating')
        dock.style.left = ''
        dock.style.top = ''
      }
    } catch {
      // 任何 layout 錯誤都不該讓 dock（及後續 render）壞掉——回到基本樣式。
      dock.classList.remove('qd-dock-vertical', 'qd-dock-floating')
      dock.style.left = ''
      dock.style.top = ''
    }
  }

  /**
   * 讓 dock 上的把手可以拖著移動 dock。跟釘住列表、carousel 的 6px 閾值一致
   * ——沒動就是點（不算數）、動了才算拖。拖曳期間 setPointerCapture 讓後續
   * 事件送回 handle，就算手指滑出 dock 也追得到。
   *
   * 座標系：`dockPos` 存的是 dock 相對 `screen` 的 top/left。screen 是 `qd-root`
   * 底下的桌面容器，跟 canvas 同座標系；用 `getBoundingClientRect` 差值算出
   * screen-local 就避開了 viewport / iframe 的相對定位陷阱。
   */
  private wireDockDrag(handle: HTMLElement): void {
    const THRESHOLD = 6
    let dragging = false
    let pending = false
    let originX = 0
    let originY = 0
    let baseX = 0
    let baseY = 0
    handle.addEventListener('pointerdown', (event) => {
      const dock = this.dock
      if (!dock) return
      originX = event.clientX
      originY = event.clientY
      const dockRect = dock.getBoundingClientRect()
      const screenRect = this.screen.getBoundingClientRect()
      baseX = dockRect.left - screenRect.left
      baseY = dockRect.top - screenRect.top
      pending = true
      dragging = false
    })
    handle.addEventListener('pointermove', (event) => {
      const dock = this.dock
      if (!dock) return
      if (pending && !dragging) {
        const dx = event.clientX - originX
        const dy = event.clientY - originY
        if (Math.hypot(dx, dy) < THRESHOLD) return
        pending = false
        dragging = true
        try { handle.setPointerCapture(event.pointerId) } catch {}
      }
      if (!dragging) return
      const screenRect = this.screen.getBoundingClientRect()
      const dockRect = dock.getBoundingClientRect()
      const maxX = Math.max(0, screenRect.width - dockRect.width)
      const maxY = Math.max(0, screenRect.height - dockRect.height)
      const x = Math.max(0, Math.min(maxX, baseX + (event.clientX - originX)))
      const y = Math.max(0, Math.min(maxY, baseY + (event.clientY - originY)))
      this.dockPos = { x: Math.round(x), y: Math.round(y) }
      dock.classList.add('qd-dock-floating')
      dock.style.left = `${this.dockPos.x}px`
      dock.style.top = `${this.dockPos.y}px`
    })
    const stop = (event: PointerEvent): void => {
      pending = false
      if (!dragging) return
      dragging = false
      try { handle.releasePointerCapture(event.pointerId) } catch {}
      if (this.dockPos) {
        try { localStorage.setItem(this.dockPosKey(), JSON.stringify(this.dockPos)) } catch {}
      }
    }
    handle.addEventListener('pointerup', stop)
    handle.addEventListener('pointercancel', stop)
  }

  /** dock 那顆單一桌面膠囊——tap 呼叫 `onSwitchDesktop` 讓宿主去挑桌面。 */
  private renderDesktopChip(dock: HTMLDivElement): void {
    if (!this.account) return
    const swap = this.onSwitchDesktop
    const chip = document.createElement(swap ? 'button' : 'span')
    const warn = !this.account.paired
    chip.className = `qd-pill qd-who${warn ? ' qd-warn' : ''}${swap ? ' qd-swap' : ''}`
    if (swap) {
      chip.addEventListener('click', () => {
        void this.flushSave()
          .catch(() => {})
          .then(() => swap())
      })
    }
    /*
      膠囊上只寫**桌面的名字**。

      以前是「誰 · 在哪」，而「在哪」在沒有桌面名稱時退回工作空間。那份設計是為
      了一個有滑鼠、有空間的畫面；在一支手機的底部，那條字長到會把旁邊的按鈕擠
      掉，而它回答的又不是這裡最常被問的問題——按這顆膠囊是為了**換一張桌面**，
      所以它該說的就是「現在是哪一張」。

      工作空間**不再退回顯示**。它不是這顆按鈕的主題：同一個工作空間底下的三張
      桌面，寫工作空間名稱三顆長得一模一樣，那正是要分辨的那件事。沒有桌面名稱
      時退回使用者名稱，膠囊才不會變成一顆空白的東西。

      名字仍然查得到——完整的身分、工作空間與 id 都在 title 上，那是滑過去才出
      現的細節，不佔那條列的寬度。
    */
    const where = this.account.desktop || this.account.user || this.account.email || '?'
    chip.textContent = `${ellipsis(where, CHIP_NAME_MAX)}${
      warn ? ' · ' + this.t('notPaired') : ''
    }${swap ? ' ⇄' : ''}`
    const who = `${this.account.email}\n${this.t('workspace')}: ${this.account.workspace} (${this.account.workspace_id})`
    chip.title = swap
      ? `${who}\n${this.t('switchDesktop')}`
      : warn
        ? `${who}\n${this.t('pairFailed')}`
        : who
    dock.append(chip)
  }

  private isPinned(uid: string): boolean {
    return this.bus.read(`app/${uid}/$window/locked`)?.value === true
  }

  /** 釘得住的視窗。`hidden` 的無頭 app 沒有框，釘它沒有意義。 */
  private pinnable(): [string, WindowView][] {
    return [...this.windows].filter(
      ([uid]) => this.bus.read(`app/${uid}/$window/state`)?.value !== 'hidden',
    )
  }

  private openPinList(anchor: HTMLElement): void {
    // 隱形遮罩:先鋪一片吃掉外面的第一次點擊,直接關列表——桌布跟 iframe 都收
    // 得到。iframe 內部的 pointerdown 不會冒泡到父 document,單靠外面的
    // pointerdown 或 focusin 有時候抓不到(某些 WKWebView 版本 focusin 不 fire)。
    const scrim = document.createElement('div')
    scrim.className = 'qd-scrim'
    scrim.addEventListener('pointerdown', (event) => {
      event.stopPropagation()
      this.closePinList()
    })
    this.screen.append(scrim)
    this.pinListScrim = scrim

    const panel = document.createElement('div')
    panel.className = 'qd-pinlist'
    this.screen.append(panel)
    this.pinList = panel
    this.fillPinList(panel)

    // 貼著 dock 往上長，右緣對齊那顆按鈕
    const box = this.screen.getBoundingClientRect()
    const at = anchor.getBoundingClientRect()
    const size = panel.getBoundingClientRect()
    panel.style.left = `${Math.max(8, Math.min(at.right - box.left - size.width, box.width - size.width - 8))}px`
    panel.style.top = `${Math.max(8, at.top - box.top - size.height - 8)}px`

    const away = (event: Event): void => {
      if (panel.contains(event.target as Node) || anchor.contains(event.target as Node)) return
      this.closePinList()
    }
    const esc = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') this.closePinList()
    }
    // 點進 iframe（元件內容）也算「點外面」。iframe click 不 bubble 到父
    // 文件，所以純靠 document pointerdown 抓不到——兩條備援：
    //   1. `focusin` on document：iframe 被點會拿 focus，focusin bubble 出來
    //   2. `window.blur`：整體失焦時保險
    // setTimeout 拉到下一 tick 掛，避開剛開時的 focus 抖動。
    const dismissOnFocusIn = (event: FocusEvent): void => {
      const target = event.target as Element | null
      if (panel.contains(target) || anchor.contains(target as Node)) return
      this.closePinList()
    }
    const dismissOnBlur = (): void => this.closePinList()
    const attachAway = setTimeout(() => {
      document.addEventListener('focusin', dismissOnFocusIn, true)
      addEventListener('blur', dismissOnBlur)
    }, 0)
    document.addEventListener('pointerdown', away, true)
    document.addEventListener('keydown', esc)
    this.pinListOff = () => {
      document.removeEventListener('pointerdown', away, true)
      document.removeEventListener('keydown', esc)
      clearTimeout(attachAway)
      document.removeEventListener('focusin', dismissOnFocusIn, true)
      removeEventListener('blur', dismissOnBlur)
    }
  }

  private fillPinList(panel: HTMLDivElement): void {
    panel.replaceChildren()
    const live = this.pinnable()
    if (live.length === 0) {
      const none = document.createElement('div')
      none.className = 'qd-pinlist-none'
      none.textContent = this.t('noWindows')
      panel.append(none)
      return
    }
    for (const [uid, view] of live) {
      const on = this.isPinned(uid)
      const row = document.createElement('div')
      row.className = 'qd-pinrow'
      const lock = document.createElement('button')
      lock.className = `qd-pinlock${on ? ' qd-on' : ''}`
      const name = document.createElement('b')
      name.textContent = view.titleText.textContent ?? uid
      const action = document.createElement('span')
      action.textContent = this.t(on ? 'unlock' : 'lock')
      lock.append(name, action)
      lock.addEventListener('click', () => {
        void this.bus.write(`app/${uid}/$window/locked`, !on, { origin: 'shell' })
      })
      // 這一列只剩鎖住／解鎖那顆——先前有一顆「縮小並置中」是給全螢幕元件當
      // 後路（title bar 被 status bar 蓋住點不到）。現在 max 態的 title bar
      // 會 padding-top 一個 safe-area 高，本來就摸得到、正常可拖，那個後路就
      // 沒必要留著，多一顆誤觸還會把使用者辛苦排好的視窗尺寸弄掉。
      row.append(lock)
      panel.append(row)
    }
  }

  private closePinList(): void {
    this.pinListOff?.()
    this.pinListOff = null
    this.pinList?.remove()
    this.pinList = null
    this.pinListScrim?.remove()
    this.pinListScrim = null
  }

  /**
   * 開說明面板。已經開著就 no-op（重複點按鈕不會重複裝訂閱、也不會疊出兩份）。
   *
   * 兩個 DOM 元素：backdrop（暗化整片、擋 click）、panel（右側 sheet）。用
   * class toggle 觸發 CSS transition——手動改 style 也可以，但 transition 那條
   * 是 backdrop opacity + panel translateX 各自的 easing，交給 CSS 定義比在
   * JS 裡算插值可維護。
   */
  private openHelp(): void {
    if (this.helpPanel) return
    const backdrop = document.createElement('div')
    backdrop.className = 'qd-help-backdrop'
    const panel = document.createElement('div')
    panel.className = 'qd-help'
    panel.setAttribute('role', 'dialog')
    panel.setAttribute('aria-modal', 'true')

    // 標題列
    const head = document.createElement('div')
    head.className = 'qd-help-head'
    const title = document.createElement('div')
    title.className = 'qd-help-title'
    title.textContent = this.t('help')
    const close = document.createElement('button')
    close.className = 'qd-help-close'
    close.textContent = '✕'
    close.title = this.t('helpClose')
    close.setAttribute('aria-label', this.t('helpClose'))
    close.addEventListener('click', () => this.closeHelp())
    head.append(title, close)

    // 內容
    const body = document.createElement('div')
    body.className = 'qd-help-body'
    for (const section of helpContent(this.locale)) {
      body.append(this.renderHelpSection(section))
    }

    panel.append(head, body)
    this.screen.append(backdrop, panel)
    this.helpBackdrop = backdrop
    this.helpPanel = panel

    // 先掛上再加 `qd-open`，才會走 CSS transition（下一個 frame 觸發）
    requestAnimationFrame(() => {
      backdrop.classList.add('qd-open')
      panel.classList.add('qd-open')
    })

    // 關閉方式：backdrop、Esc、外框 click——三條路都收在 helpOff 一起解。
    const onBackdrop = (): void => this.closeHelp()
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') this.closeHelp()
    }
    backdrop.addEventListener('click', onBackdrop)
    document.addEventListener('keydown', onKey)
    this.helpOff = () => {
      backdrop.removeEventListener('click', onBackdrop)
      document.removeEventListener('keydown', onKey)
    }
  }

  private closeHelp(): void {
    this.helpOff?.()
    this.helpOff = null
    const panel = this.helpPanel
    const backdrop = this.helpBackdrop
    if (!panel && !backdrop) return
    panel?.classList.remove('qd-open')
    backdrop?.classList.remove('qd-open')
    this.helpPanel = null
    this.helpBackdrop = null
    // 等 transition 走完再拆——直接 remove 會看不到收合動畫。220ms 對到
    // CSS transition duration，多留一點緩衝避免早收。
    setTimeout(() => {
      panel?.remove()
      backdrop?.remove()
    }, 260)
  }

  /**
   * 一個 section 對應一顆卡片。標題 + 一堆 blocks（純段落 or bullet 列表）。
   * bullet 用 ul 語意化——螢幕閱讀器讀起來也是「清單」。
   */
  private renderHelpSection(section: HelpSection): HTMLDivElement {
    const el = document.createElement('div')
    el.className = 'qd-help-section'
    const title = document.createElement('div')
    title.className = 'qd-help-section-title'
    title.textContent = section.title
    el.append(title)
    for (const block of section.blocks) {
      el.append(this.renderHelpBlock(block))
    }
    return el
  }

  private renderHelpBlock(block: HelpBlock): HTMLElement {
    if (block.kind === 'para') {
      const p = document.createElement('p')
      p.className = 'qd-help-para'
      this.applyHelpRichText(p, block.text)
      return p
    }
    const ul = document.createElement('ul')
    ul.className = 'qd-help-list'
    for (const item of block.items) {
      const li = document.createElement('li')
      li.className = 'qd-help-item'
      this.applyHelpRichText(li, item)
      ul.append(li)
    }
    return ul
  }

  /**
   * 迷你 markdown：只認 `**bold**`。掃 source 交替出 text / bold text，
   * 用 textContent 塞（不是 innerHTML）——內容是常數字串，但走 textContent
   * 一勞永逸擋住未來被外部字串汙染的機會。
   */
  private applyHelpRichText(container: HTMLElement, source: string): void {
    let cursor = 0
    let bold = false
    while (cursor < source.length) {
      const marker = source.indexOf('**', cursor)
      if (marker < 0) {
        container.append(document.createTextNode(source.substring(cursor)))
        break
      }
      if (marker > cursor) {
        const chunk = source.substring(cursor, marker)
        if (bold) {
          const strong = document.createElement('span')
          strong.className = 'qd-help-bold'
          strong.textContent = chunk
          container.append(strong)
        } else {
          container.append(document.createTextNode(chunk))
        }
      }
      cursor = marker + 2
      bold = !bold
    }
  }

}

const SVG_NS = 'http://www.w3.org/2000/svg'
const MAX_ARCS = 6

/** app/{uid}/... → uid */
/**
 * 存檔裡哪些視窗還原得回來。
 *
 * 目錄會變——上架、下架、換網址。照著存檔硬開會得到一個指向 404 的空框，而且
 * registry 裡會多一筆永遠停在 loading 的實例：對人是個沒有意義的框，對 agent
 * 更糟，它會以為那是個能用的工具。
 *
 * 比對的是**網址**而不是 uid 或標題：uid 是這台機器上產生的，標題可以改，只有
 * 網址是「這是哪一個 app」的身分（D5：app = 一個網址）。
 */
function restorable(
  instances: SessionSnapshot['instances'],
  catalog: readonly CatalogEntry[],
): { keep: SessionSnapshot['instances']; dropped: SessionSnapshot['instances'] } {
  const known = new Set(catalog.map((entry) => entry.url))
  const keep: SessionSnapshot['instances'] = []
  const dropped: SessionSnapshot['instances'] = []
  for (const instance of instances) {
    ;(known.has(instance.url) ? keep : dropped).push(instance)
  }
  return { keep, dropped }
}

/**
 * 還原時套上限：留 z 最高的那幾個。
 *
 * 留最上面的，因為那是使用者最近在用的——留最底下的等於把他正在看的東西拿走，
 * 留下一堆壓在後面的。回傳仍照原本的順序，疊放才會跟存檔時一致。
 */
function withinLimit(
  instances: SessionSnapshot['instances'],
  limit: number,
  systemUrls: ReadonlySet<string>,
): { keep: SessionSnapshot['instances']; overflow: SessionSnapshot['instances'] } {
  if (!Number.isFinite(limit) || limit < 0) return { keep: instances, overflow: [] }

  const widgets = instances.filter((instance) => !systemUrls.has(instance.url))
  if (widgets.length <= limit) return { keep: instances, overflow: [] }

  const survivors = new Set(
    [...widgets]
      .sort((a, b) => (b.z ?? 0) - (a.z ?? 0))
      .slice(0, limit)
      .map((instance) => instance.uid),
  )
  const keep: SessionSnapshot['instances'] = []
  const overflow: SessionSnapshot['instances'] = []
  for (const instance of instances) {
    // 系統 app 不算在上限裡，所以永遠留著。
    if (systemUrls.has(instance.url) || survivors.has(instance.uid)) keep.push(instance)
    else overflow.push(instance)
  }
  return { keep, overflow }
}

function uidOf(path: string): string {
  const parts = path.split('/')
  return parts[0] === 'app' ? (parts[1] ?? '') : ''
}

/**
 * 兩點之間的弧線。
 *
 * 用二次貝茲加一個垂直於連線方向的偏移——直線在視窗密集時會分不清
 * 哪條配哪條，弧線才看得出是同一條。偏移量隨距離成長但設上限，
 * 免得長距離時彎得太誇張。
 */
function arcPath(from: { x: number; y: number }, to: { x: number; y: number }): string {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const length = Math.hypot(dx, dy) || 1
  const bend = Math.min(90, length * 0.22)
  const cx = (from.x + to.x) / 2 - (dy / length) * bend
  const cy = (from.y + to.y) / 2 + (dx / length) * bend
  return `M${from.x},${from.y} Q${cx},${cy} ${to.x},${to.y}`
}

/**
 * 為什麼這段 JSON 不合法——猜最常見的那三種。
 *
 * 幾乎每一次都是從程式碼裡複製過來的：JavaScript 的物件字面值長得像 JSON，但
 * 鍵名不用引號、可以用單引號、結尾可以多一個逗號。`JSON.parse` 給的位置訊息
 * 對懂的人夠用，對正在貼上的人只是一串座標。
 *
 * **只提示，不自動修。** 用正規表示式改寫別人的資料，錯的時候是默默存進一筆
 * 不一樣的東西——那比一則錯誤訊息糟得多。
 */
function jsonHint(text: string, t: (key: ShellKey) => string): string {
  if (/'/.test(text)) return t('jsonSingleQuote')
  if (/[{,]\s*[A-Za-z_$一-鿿][\w$一-鿿]*\s*:/.test(text)) return t('jsonBareKey')
  if (/,\s*[}\]]/.test(text)) return t('jsonTrailingComma')
  return ''
}

/**
 * 結構化的值在輸入框裡長什麼樣。
 *
 * 沒有值就給空字串而不是 "null"：那一格是空的，才看得出「還沒設過」。
 */
function jsonText(value: unknown): string {
  if (value === null || value === undefined) return ''
  try {
    return JSON.stringify(value)
  } catch {
    return ''
  }
}

/**
 * 一顆圖示按鈕。
 *
 * 標題存成 data-i18n-title，換語言時 applyLocale 靠它掃一遍就能重貼——不然每個
 * 視窗要多存七個 DOM 參照，只為了改一行提示文字。
 */
function button(
  className: string,
  label: string,
  t: (key: ShellKey) => string,
  titleKey: ShellKey,
): HTMLButtonElement {
  const el = document.createElement('button')
  el.className = className
  el.textContent = label
  el.title = t(titleKey)
  el.dataset['i18nTitle'] = titleKey
  return el
}

/**
 * 從起始矩形與位移算出新的矩形。純函式，方便推理。
 *
 * 往左／往上縮放時，w/h 變小的同時 x/y 要跟著移動，否則對邊會跑掉。
 * 縮到下限時把 x/y 釘在對邊，不然會繼續往回推。
 */
function resolveResize(start: WindowRect, dir: string, dx: number, dy: number): WindowRect {
  let { x, y, w, h } = start

  if (dir.includes('e')) w = start.w + dx
  if (dir.includes('s')) h = start.h + dy
  if (dir.includes('w')) {
    w = start.w - dx
    x = start.x + dx
  }
  if (dir.includes('n')) {
    h = start.h - dy
    y = start.y + dy
  }

  if (w < MIN_W) {
    if (dir.includes('w')) x = start.x + start.w - MIN_W
    w = MIN_W
  }
  if (h < MIN_H) {
    if (dir.includes('n')) y = start.y + start.h - MIN_H
    h = MIN_H
  }

  return { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) }
}

export { arcPath, resolveResize, restorable, uidOf }
export type { BusError }
